<template>
  <div
    ref="dragContainer"
    style="max-width: 1600px"
    class="mx-auto pl-3 h-full"
    :class="isMobile ? 'pl-4' : 'md:pl-4'"
    @dragover="onDragover"
    @drop="isDragFile = false"
  >
    <HoverDropzone
      v-if="sortedDocuments.length && withUploadButton && editable"
      :is-dragging="isDragFile"
      :template-id="template.id"
      :accept-file-types="acceptFileTypes"
      :with-replace-and-clone="withReplaceAndCloneUpload"
      @add="[updateFromUpload($event), isDragFile = false]"
      @replace="[onDocumentsReplace($event), isDragFile = false]"
      @replace-and-clone="onDocumentsReplaceAndTemplateClone($event)"
      @error="[onUploadFailed($event), isDragFile = false]"
    />
    <DragPlaceholder
      ref="dragPlaceholder"
      :field="fieldsDragFieldRef.value || toRaw(dragField)"
      :is-field="template.fields.includes(fieldsDragFieldRef.value)"
      :is-default="defaultFields.includes(toRaw(dragField))"
      :is-required="defaultRequiredFields.includes(toRaw(dragField))"
    />
    <div
      v-if="pendingFieldAttachmentUuids.length && editable"
      class="top-1.5 sticky h-0 z-20 max-w-2xl mx-auto"
    >
      <div class="alert border-base-content/30 py-2 px-2.5">
        <IconInfoCircle
          class="stroke-info shrink-0 w-6 h-6"
        />
        <span>{{ t('uploaded_pdf_contains_form_fields_keep_or_remove_them') }}</span>
        <div>
          <button
            class="btn btn-sm"
            @click.prevent="removePendingFields"
          >
            {{ t('remove') }}
          </button>
          <button
            class="btn btn-sm btn-neutral text-white"
            @click.prevent="save"
          >
            {{ t('keep') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="$slots.buttons || withTitle"
      id="title_container"
      class="flex justify-between py-1.5 items-center pr-4 top-0 z-10 title-container"
      :class="{ sticky: withStickySubmitters || isBreakpointLg }"
      :style="{ backgroundColor }"
    >
      <div class="flex items-center space-x-3 min-w-0">
        <a
          v-if="withLogo"
          href="/"
        >
          <Logo />
        </a>
        <!-- The builder renders with the "plain" layout, which has no navbar, so without this
             the only way out is the browser back button. -->
        <a
          :href="envelopeId ? '/envelopes' : '/templates'"
          class="base-button flex items-center gap-1 flex-shrink-0"
          :title="envelopeId ? 'Back to envelopes' : 'Back to templates'"
          @click="leaveBuilder"
        >
          <IconArrowLeft :width="18" :height="18" />
          <span class="hidden md:inline">{{ envelopeId ? 'Envelopes' : 'Templates' }}</span>
        </a>
        <Contenteditable
          v-if="withTitle"
          :model-value="displayName"
          :editable="editable"
          class="text-xl md:text-3xl font-semibold focus:text-clip template-name"
          :icon-stroke-width="2.3"
          :title="template.name"
          @update:model-value="updateName"
        />
        <!-- Autosave failures were previously silent; surface them where the work is happening. -->
        <button
          v-if="saveError"
          class="flex items-center gap-1 flex-shrink-0 text-sm font-medium text-red-600 border border-red-300 rounded-lg px-2 py-1"
          :title="saveError"
          @click="save()"
        >
          <IconAlertTriangle :width="16" :height="16" />
          <span class="hidden md:inline">Not saved — retry</span>
        </button>
      </div>
      <div class="space-x-3 flex items-center flex-shrink-0">
        <slot
          v-if="$slots.buttons"
          name="buttons"
        />
        <template v-else>
          <form
            v-if="withSignYourselfButton && template.submitters.length < 2"
            target="_blank"
            data-turbo="false"
            class="inline"
            method="post"
            :action="`/d/${template.slug}`"
            @submit="maybeShowErrorTemplateAlert"
          >
            <input
              type="hidden"
              name="_method"
              value="put"
              autocomplete="off"
            >
            <input
              type="hidden"
              name="authenticity_token"
              :value="authenticityToken"
              autocomplete="off"
            >
            <input
              type="hidden"
              name="selfsign"
              value="true"
              autocomplete="off"
            >
            <button
              class="btn btn-primary btn-ghost text-base hidden md:flex"
              type="submit"
            >
              <IconWritingSign
                width="22"
                class="inline"
              />
              <span class="hidden md:inline">
                {{ t('sign_yourself') }}
              </span>
            </button>
          </form>
          <a
            v-else-if="withSignYourselfButton"
            id="sign_yourself_button"
            :href="`/templates/${template.id}/submissions/new?selfsign=true`"
            class="btn btn-primary btn-ghost text-base hidden md:flex"
            data-turbo-frame="modal"
            @click="maybeShowErrorTemplateAlert"
          >
            <IconWritingSign
              width="22"
              class="inline"
            />
            <span class="hidden md:inline">
              {{ t('sign_yourself') }}
            </span>
          </a>
          <div v-if="envelopeId" style="position:relative;">
            <button
              class="btn btn-ghost btn-sm hidden md:flex items-center gap-1"
              style="border:1px solid #4B5563;font-size:12px;font-weight:600;"
              @click="isActionsDropdown = !isActionsDropdown"
            >
              Actions
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div v-if="isActionsDropdown"
              style="position:absolute;top:calc(100% + 6px);right:0;background:#ffffff;border:1px solid #e2e8f0;border-radius:10px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.07),0 10px 32px -4px rgba(0,0,0,0.12);min-width:220px;z-index:1000;overflow:hidden;padding:4px 0;"
              @click.stop>
            <div style="position:fixed;inset:0;z-index:-1;" @click="isActionsDropdown = false"></div>
            <div style="padding:8px 12px 6px;border-bottom:1px solid #f1f5f9;margin-bottom:2px;">
              <span style="font-size:10px;font-weight:700;color:#94a3b8;letter-spacing:.8px;text-transform:uppercase;">Envelope Actions</span>
            </div>
            <button @click="openEditMessage" @mouseenter="hoveredAction='msg'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='msg'?'#f8fafc':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#1e293b',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <span>Edit Message</span>
            </button>
            <button @click="openEditRecipients" @mouseenter="hoveredAction='rec'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='rec'?'#f8fafc':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#1e293b',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <span>Edit Recipients</span>
            </button>
            <button @click="editDocuments" @mouseenter="hoveredAction='doc'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='doc'?'#f8fafc':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#1e293b',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span>Edit Documents</span>
            </button>
            <button @click="isAdvancedOptionsModal = true" @mouseenter="hoveredAction='adv'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='adv'?'#f8fafc':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#1e293b',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
              <span>Advanced Options</span>
            </button>
            <div style="height:1px;background:#f1f5f9;margin:4px 0;"></div>
            <button @click="closeBuilder" @mouseenter="hoveredAction='dis'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='dis'?'#f8fafc':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#1e293b',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              <span>Close</span>
            </button>
            <div style="height:1px;background:#f1f5f9;margin:4px 0;"></div>
            <button @click="deleteEnvelope" @mouseenter="hoveredAction='del'" @mouseleave="hoveredAction=''" :style="{width:'100%',padding:'8px 14px',border:'none',background:hoveredAction==='del'?'#fef2f2':'transparent',textAlign:'left',cursor:'pointer',fontSize:'13px',color:'#ef4444',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'10px'}">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              <span>Delete</span>
            </button>
            <div style="padding:4px 0 2px;"></div>
          </div>
          </div>
          <button
            v-if="!envelopeId"
            class="btn btn-ghost btn-sm hidden md:flex items-center gap-1"
            style="border:1px solid #4B5563;font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;"
            @click="isAdvancedOptionsModal = true"
          >
            ADVANCED OPTIONS
          </button>
          <a
            v-if="withSendButton"
            id="send_button"
            :href="`/templates/${template.id}/submissions/new?with_link=true`"
            data-turbo-frame="modal"
            class="white-button md:!px-6"
            @click="maybeShowErrorTemplateAlert"
          >
            <IconUsersPlus
              width="20"
              class="inline"
            />
            <span class="hidden md:inline">
              {{ t('send') }}
            </span>
          </a>
          <button
            v-if="editable"
            class="btn btn-ghost btn-sm hidden md:flex items-center gap-1"
            @click="isShortcutsModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/></svg>
            <span class="text-sm">Shortcuts</span>
          </button>
          <span
            v-if="editable"
            id="save_button_container"
            class="flex"
          >
            <button
              class="base-button !rounded-r-none !pr-2"
              :class="{ disabled: isSaving }"
              v-bind="isSaving ? { disabled: true } : {}"
              @click.prevent="onSaveClick"
            >
              <IconInnerShadowTop
                v-if="isSaving"
                width="22"
                class="animate-spin"
              />
              <IconDeviceFloppy
                v-else
                width="22"
              />
              <span class="hidden md:inline">
                {{ t('save') }}
              </span>
            </button>
            <div
              class="dropdown dropdown-end"
              :class="{ 'dropdown-open': isDownloading }"
            >
              <label
                tabindex="0"
                class="base-button !rounded-l-none !pl-1 !pr-2 !border-l-neutral-500"
              >
                <span class="text-sm align-text-top">
                  <IconChevronDown class="w-5 h-5 flex-shrink-0" />
                </span>
              </label>
              <ul
                tabindex="0"
                class="dropdown-content p-2 mt-2 shadow menu text-base bg-base-100 rounded-box text-right"
              >
                <li>
                  <a
                    :href="`/templates/${template.id}/form`"
                    data-turbo="false"
                    class="flex items-center justify-center space-x-2"
                  >
                    <IconEye class="w-6 h-6 flex-shrink-0" />
                    <span class="whitespace-nowrap">{{ t('save_and_preview') }}</span>
                  </a>
                </li>
                <li>
                  <a
                    :href="`/templates/${template.id}/preferences`"
                    data-turbo-frame="modal"
                    class="flex space-x-2"
                    @click="closeDropdown"
                  >
                    <IconAdjustments class="w-6 h-6 flex-shrink-0" />
                    <span class="whitespace-nowrap">{{ t('preferences') }}</span>
                  </a>
                </li>
                <li v-if="withDownload">
                  <button
                    class="flex space-x-2"
                    :disabled="isDownloading"
                    @click.stop.prevent="download"
                  >
                    <IconInnerShadowTop
                      v-if="isDownloading"
                      class="animate-spin w-6 h-6 flex-shrink-0"
                    />
                    <IconDownload
                      v-else
                      class="w-6 h-6 flex-shrink-0"
                    />
                    <span
                      v-if="isDownloading"
                      class="whitespace-nowrap"
                    >{{ t('downloading_') }}</span>
                    <span
                      v-else
                      class="whitespace-nowrap"
                    >{{ t('download') }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </span>
          <a
            v-else
            :href="`/templates/${template.id}`"
            class="base-button"
          >
            <span class="hidden md:inline">
              {{ t('back') }}
            </span>
          </a>
        </template>
      </div>
    </div>
    <div
      id="main_container"
      class="flex main-container"
      :class="$slots.buttons || withTitle ? (isMobile ? 'max-h-[calc(100%_-_60px)]' : 'md:max-h-[calc(100%_-_60px)]') : (isMobile ? 'max-h-[100%]' : 'md:max-h-[100%]')"
    >
      <div
        v-if="withFieldsList && !isMobile"
        id="fields_list_container"
        class="relative w-80 flex-none mt-1 pr-4 pl-0.5 hidden md:block fields-list-container overflow-y-auto overflow-x-hidden"
      >
        <!-- Place mode. This used to be a full-height panel covering the whole field list, which
             read as "drawing is the only way in" and hid every other field type until you
             cancelled. A single click on the page already drops a default-sized box (onDraw fills
             a zero-size area from setDefaultAreaSize), so the bar just says so and stays out of
             the way. -->
        <div
          v-if="showDrawField || drawField"
          class="sticky top-0 z-20 pb-2"
          :style="{ backgroundColor }"
        >
          <div class="bg-base-200 rounded-lg px-3 py-2.5 draw-field-container">
            <div class="flex items-start gap-2">
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium leading-snug">
                  <span v-if="(drawField?.type || drawFieldType) === 'strikethrough'">Click on the document to strike out</span>
                  <span v-else>Click on the document to place {{ placeFieldName }}</span>
                </p>
                <p class="text-xs opacity-60 mt-0.5">
                  or drag to size it &middot; Esc to cancel
                </p>
              </div>
              <button
                class="btn btn-xs btn-ghost cancel-draw-button"
                @click="clearDrawField"
              >
                {{ t('cancel') }}
              </button>
            </div>
            <!-- Still reachable: a field with no area at all, for anything filled outside the
                 document (API, prefill) rather than signed on it. -->
            <a
              v-if="!drawField && !drawOption && !['stamp', 'signature', 'initials', 'heading', 'strikethrough'].includes(drawField?.type || drawFieldType)"
              href="#"
              class="link text-xs mt-2 inline-block"
              @click.prevent="[addField(drawFieldType), drawField = null, drawOption = null, withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]"
            >
              {{ t('or_add_field_without_drawing') }}
            </a>
          </div>
        </div>
        <div>
          <Fields
            ref="fields"
            :fields="template.fields"
            :submitters="template.submitters"
            :selected-submitter="selectedSubmitter"
            :with-help="withHelp"
            :default-submitters="defaultSubmitters"
            :draw-field-type="drawFieldType"
            :with-fields-search="withFieldsSearch"
            :default-fields="[...defaultRequiredFields, ...defaultFields]"
            :template="template"
            :default-required-fields="defaultRequiredFields"
            :field-types="fieldTypes"
            :with-sticky-submitters="withStickySubmitters"
            :with-fields-detection="withFieldsDetection"
            :with-signature-id="withSignatureId"
            :with-prefillable="withPrefillable"
            :only-defined-fields="onlyDefinedFields"
            :editable="editable"
            :show-tour-start-form="showTourStartForm"
            @add-field="addField"
            @add-field-in-view="addFieldToPageInView"
            @set-draw="[drawField = $event.field, drawOption = $event.option]"
            @select-submitter="selectedSubmitter = $event"
            @set-draw-type="[drawFieldType = $event, showDrawField = true]"
            @set-smart-field="[pendingSmartField = $event, drawFieldType = $event.type]"
            @set-drag="dragField = $event"
            @set-drag-placeholder="$refs.dragPlaceholder.dragPlaceholder = $event"
            @change-submitter="selectedSubmitter = $event"
            @drag-end="[dragField = null, $refs.dragPlaceholder.dragPlaceholder = null]"
            @scroll-to-area="scrollToArea"
          />
        </div>
      </div>
      <div class="flex flex-col w-full min-w-0">
        <!-- No field-type strip above the document: the sidebar palette is a DocuSign-style
             one-row-per-type list now, which is the same reach in one place. The strip was a
             second copy of that palette and roughly 40 lines of duplicated drag/click policy
             with it. -->
        <div
          id="pages_container"
          class="w-full mt-0.5 pt-0.5 flex-1 min-h-0"
          :class="[
            isMobile ? 'overflow-y-auto' : 'overflow-y-hidden md:overflow-y-auto',
            zoom > 1 ? 'overflow-x-auto pages-zoomed' : 'overflow-x-hidden'
          ]"
          @click.self="[selectedAreaRef.value = null, selectedPanelField = null]"
        >
          <div
            v-if="editable && sortedDocuments.length"
            class="sticky top-1 z-30 flex justify-end pr-3.5 pointer-events-none"
          >
            <div class="join shadow-md bg-base-100 border border-base-300 pointer-events-auto">
              <button
                type="button"
                class="btn btn-sm btn-ghost join-item px-2"
                :disabled="zoom <= 0.5"
                title="Zoom out"
                @click.prevent="zoomOut"
              >
                <IconMinus class="w-4 h-4" />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-ghost join-item px-1 w-14 tabular-nums"
                title="Reset zoom"
                @click.prevent="resetZoom"
              >
                {{ Math.round(zoom * 100) }}%
              </button>
              <button
                type="button"
                class="btn btn-sm btn-ghost join-item px-2"
                :disabled="zoom >= 3"
                title="Zoom in"
                @click.prevent="zoomIn"
              >
                <IconPlus class="w-4 h-4" />
              </button>
            </div>
          </div>
          <div
            ref="documents"
            class="pr-3.5 pl-0.5"
            :style="{ width: (zoom * 100) + '%' }"
          >
            <template v-if="!sortedDocuments.length && (withUploadButton || withAddPageButton)">
              <Dropzone
                v-if="withUploadButton"
                :template-id="template.id"
                :accept-file-types="acceptFileTypes"
                :with-google-drive="withGoogleDrive"
                @click-google-drive="$refs.upload.openGoogleDriveModal()"
                @success="updateFromUpload"
              />
              <button
                v-if="withAddPageButton"
                id="add_blank_page_button"
                class="btn btn-outline w-full mt-4 add-blank-page-button"
                @click.prevent="addBlankPage"
              >
                <IconInnerShadowTop
                  v-if="isLoadingBlankPage"
                  class="animate-spin w-5 h-5"
                />
                <IconPlus
                  v-else
                  class="w-5 h-5"
                />
                {{ t('add_blank_page') }}
              </button>
            </template>
            <template v-else>
              <template
                v-for="document in sortedDocuments"
                :key="document.uuid"
              >
                <Document
                  :ref="setDocumentRefs"
                  :areas-index="fieldAreasIndex[document.uuid]"
                  :selected-submitter="selectedSubmitter"
                  :document="document"
                  :is-drag="!!dragField"
                  :input-mode="inputMode"
                  :default-fields="[...defaultRequiredFields, ...defaultFields]"
                  :allow-draw="!onlyDefinedFields || drawField"
                  :with-signature-id="withSignatureId"
                  :with-prefillable="withPrefillable"
                  :data-document-uuid="document.uuid"
                  :default-submitters="defaultSubmitters"
                  :drag-field-placeholder="fieldsDragFieldRef.value || dragField"
                  :with-field-placeholder="withFieldPlaceholder"
                  :draw-field="drawField"
                  :draw-field-type="drawFieldType"
                  :editable="editable"
                  :base-url="baseUrl"
                  @draw="[onDraw($event), withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]"
                  @drop-field="onDropfield"
                  @remove-area="removeArea"
                  @duplicate-fields="onDuplicateFields"
                  @move-duplicated-fields="onMoveDuplicatedFields"
                />
                <DocumentControls
                  v-if="isBreakpointLg && editable"
                  :with-arrows="template.schema.length > 1"
                  :item="template.schema.find((item) => item.attachment_uuid === document.uuid)"
                  :with-replace-button="withUploadButton"
                  :accept-file-types="acceptFileTypes"
                  :document="document"
                  :template="template"
                  class="pb-2 mb-2 border-b border-base-300 border-dashed"
                  @remove="onDocumentRemove"
                  @replace="onDocumentReplace"
                  @up="moveDocument(template.schema.find((item) => item.attachment_uuid === document.uuid), -1)"
                  @down="moveDocument(template.schema.find((item) => item.attachment_uuid === document.uuid), 1)"
                  @change="save"
                />
              </template>
              <div
                v-if="sortedDocuments.length && isBreakpointLg && editable"
                class="pb-4 space-y-2"
              >
                <Upload
                  v-if="withUploadButton"
                  :template-id="template.id"
                  :accept-file-types="acceptFileTypes"
                  :authenticity-token="authenticityToken"
                  :with-google-drive="withGoogleDrive"
                  @success="updateFromUpload"
                />
                <button
                  v-if="withAddPageButton"
                  id="add_blank_page_button"
                  class="btn btn-outline w-full mt-4 add-blank-page-button"
                  @click.prevent="addBlankPage"
                >
                  <IconInnerShadowTop
                    v-if="isLoadingBlankPage"
                    class="animate-spin w-5 h-5"
                  />
                  <IconPlus
                    v-else
                    class="w-5 h-5"
                  />
                  {{ t('add_blank_page') }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="withDocumentsList || withFieldsList"
        id="documents_container"
        ref="previews"
        :style="{ 'display': isBreakpointLg ? 'none' : 'initial' }"
        class="overflow-y-auto overflow-x-hidden w-72 flex-none pr-3 mt-0.5 pt-0.5 hidden lg:block right-panel"
      >
        <div class="flex items-center justify-between mb-2 px-1 pt-1">
          <span style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;">
            {{ selectedPanelField ? fieldPanelTitle : (alignmentFieldUuids.value && alignmentFieldUuids.value.size >= 2 ? 'Align Fields' : (selectedFieldUuids.value && selectedFieldUuids.value.size > 0 ? 'Bulk Edit' : 'Document')) }}
          </span>
          <button
            v-if="!selectedPanelField"
            class="btn btn-xs btn-ghost"
            @click="isPreviewCollapsed = !isPreviewCollapsed"
          >
            <svg v-if="isPreviewCollapsed" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button
            v-else
            style="border:none;background:none;cursor:pointer;color:#9ca3af;padding:2px 4px;border-radius:4px;line-height:1;display:inline-flex;"
            @mouseenter="$event.currentTarget.style.color='#374151'"
            @mouseleave="$event.currentTarget.style.color='#9ca3af'"
            @click="selectedPanelField = null"
          ><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg></button>
        </div>
        <!-- Signers and the required-field checklist sit in the right panel's resting state, so
             neither needs a field selected on the document first: this is where a signer is
             deleted, and where an unfilled required field is jumped to and given its value. -->
        <div
          v-if="!selectedPanelField && !(selectedFieldUuids.value && selectedFieldUuids.value.size > 0)"
          class="px-1"
          style="margin-bottom:12px;"
        >
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;">Signers</div>
            <!-- DocuSign's "Set signing order". On: each signer carries a routing number and is
                 invited only once everyone with a lower number has finished. Off: everyone is
                 invited at once. -->
            <label
              v-if="editable && template.submitters.length > 1"
              style="display:flex;align-items:center;gap:5px;cursor:pointer;font-size:10px;color:#64748b;"
              title="Recipients are invited in the order below. Give two signers the same number to invite them together."
            >
              <input
                type="checkbox"
                :checked="signingOrderEnabled"
                :disabled="signingOrderSaving"
                style="width:12px;height:12px;accent-color:var(--brand, #1E3A6E);cursor:pointer;"
                @change="toggleSigningOrder($event.target.checked)"
              >
              Signing order
            </label>
          </div>
          <div style="display:flex;flex-direction:column;gap:4px;">
            <div
              v-for="(submitter, index) in template.submitters"
              :key="submitter.uuid"
              style="display:flex;flex-direction:column;gap:2px;padding:7px 10px;border:1.5px solid #e5e7eb;border-radius:7px;background:#fff;"
            >
              <div style="display:flex;align-items:center;gap:8px;">
                <!-- The routing number itself, typed rather than dragged: this is a NUMBER, not
                     a list position, and neither template.submitters nor envelope_recipients may
                     be reordered (both are paired by position -- see Envelopes::SendSubmission
                     and envelope_recipients' `-> { order(:id) }` scope), so moving a row here
                     would hand one signer another's role. -->
                <input
                  v-if="signingOrderEnabled"
                  type="number"
                  min="1"
                  :max="template.submitters.length"
                  :value="signerOrders[index]"
                  :disabled="!editable || signingOrderSaving"
                  title="Signing order. Same number as another signer means they sign at the same time."
                  style="width:34px;flex-shrink:0;padding:2px 4px;border:1px solid #d1d5db;border-radius:5px;font-size:11px;font-weight:700;color:#1E3A6E;text-align:center;font-family:inherit;background:#fff;"
                  @change="setSignerOrder(index, $event.target.value)"
                >
                <span :style="{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: '0', background: submitterColors[index % submitterColors.length] }" />
                <span style="font-size:13px;font-weight:500;color:#152C54;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ submitter.name }}</span>
                <!-- The last signer stays: removing it would leave every field pointing at nobody. -->
                <button
                  v-if="editable && template.submitters.length > 1"
                  title="Delete signer"
                  style="border:none;background:none;cursor:pointer;color:#ef4444;padding:2px;display:inline-flex;flex-shrink:0;"
                  @click.prevent="removeSubmitter(submitter)"
                >
                  <IconTrashX :width="15" :height="15" />
                </button>
              </div>
              <!-- Action type, DocuSign's per-recipient one. Read-only: it lives on the envelope
                   recipient, so the Envelope creator and Edit recipients are what set it.
                   Its own line rather than a trailing chip -- the panel is ~210px wide, and
                   sharing the row cut "ApTask Onboarding" down to "ApTask O...". -->
              <span
                v-if="submitterActionTypes[submitter.uuid]"
                style="font-size:9px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;color:#1E3A6E;padding-left:18px;"
                title="Set on the envelope; change it in Edit recipients"
              >{{ submitterActionTypes[submitter.uuid] }}</span>
            </div>
          </div>
          <!-- Plain template: submitters are roles, and no action type exists on them until an
               envelope is created, so this says where it gets chosen instead of stamping every
               row with a "Needs to Sign" that nothing stores. -->
          <div
            v-if="!envelopeData"
            style="font-size:10px;line-height:1.45;color:#64748b;margin-top:6px;padding:0 2px;"
          >
            Needs to Sign / Receives a Copy is chosen per recipient when this template is sent.
          </div>
          <!-- Multi-select is Ctrl/Cmd-click on a field or a Ctrl/Cmd-drag region on the page
               (area.vue's onCtrlClick, page.vue's onStartDraw), and it swaps this panel for the
               Bulk Edit one with its Recipient dropdown. Both gestures were unguessable with
               nothing on screen naming them. Held back until there are two fields to select --
               on an empty document it would be advice about nothing. -->
          <div
            v-if="editable && template.fields && template.fields.length >= 2"
            style="font-size:10px;line-height:1.45;color:#64748b;margin-top:6px;padding:0 2px;"
          >
            {{ isMac ? 'Cmd' : 'Ctrl' }}-click fields to select several, then assign them to a
            recipient. {{ isMac ? 'Cmd' : 'Ctrl' }}-drag on the page selects a region.
          </div>
          <template v-if="nonSigningRecipients.length">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;margin:14px 0 6px;">Copied / Viewers</div>
            <div style="display:flex;flex-direction:column;gap:4px;">
              <!-- No colour dot and no delete: these recipients own no fields, so there is no
                   hue keyed to them and nothing in this template to remove. -->
              <div
                v-for="recipient in nonSigningRecipients"
                :key="recipient.id"
                style="display:flex;align-items:center;gap:8px;padding:7px 10px;border:1.5px solid #e5e7eb;border-radius:7px;background:#fff;"
              >
                <span style="font-size:13px;font-weight:500;color:#152C54;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ recipient.name || recipient.email }}</span>
                <span style="font-size:9px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;flex-shrink:0;white-space:nowrap;color:#1E3A6E;">{{ recipientRoleLabels[recipient.role] || recipient.role }}</span>
              </div>
            </div>
          </template>
          <template v-if="requiredPanelFields.length">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;margin:14px 0 6px;">Required Fields</div>
            <div style="display:flex;flex-direction:column;gap:4px;">
              <div
                v-for="field in requiredPanelFields"
                :key="field.uuid"
                style="border:1.5px solid #e5e7eb;border-radius:7px;background:#fff;padding:6px 8px;"
              >
                <div style="display:flex;align-items:center;gap:7px;cursor:pointer;" @click="scrollToRequiredField(field)">
                  <!-- A field whose signer was deleted has no index; fall back to the first hue
                       rather than indexing the palette with -1 and painting nothing. -->
                  <span :style="{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: '0', background: submitterColors[Math.max(0, template.submitters.findIndex((s) => s.uuid === field.submitter_uuid)) % submitterColors.length] }" />
                  <span style="font-size:12px;color:#152C54;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ field.name || buildDefaultFieldName(field, template.fields) }}</span>
                  <span :style="{ fontSize: '9px', fontWeight: '700', letterSpacing: '.4px', textTransform: 'uppercase', flexShrink: '0', color: field.default_value ? '#16a34a' : '#f59e0b' }">{{ field.default_value ? 'Filled' : 'Empty' }}</span>
                </div>
                <!-- Only the types whose value is plain text can be typed here; a signature or an
                     upload has to be produced on the document itself, so those rows are jump-only. -->
                <input
                  v-if="editable && prefillableTypes.includes(field.type)"
                  class="input input-bordered input-xs w-full bg-transparent"
                  style="margin-top:5px;"
                  :value="field.default_value"
                  :placeholder="'Prefill ' + (field.name || field.type)"
                  @blur="setRequiredFieldValue(field, $event.target.value)"
                  @keydown.enter.prevent="$event.target.blur()"
                >
              </div>
            </div>
          </template>
        </div>
        <div v-if="selectedPanelField" class="px-1">
          <div v-if="template.submitters && template.submitters.length > 1" style="margin-bottom:10px;">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;margin-bottom:6px;">Recipient</div>
            <div style="display:flex;flex-direction:column;gap:4px;">
              <div
                v-for="(submitter, index) in template.submitters"
                :key="submitter.uuid"
                @click="[selectedPanelField.submitter_uuid = submitter.uuid, save()]"
                :style="{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 10px',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  border: selectedPanelField.submitter_uuid === submitter.uuid ? '1.5px solid var(--brand, #1E3A6E)' : '1.5px solid #e5e7eb',
                  background: selectedPanelField.submitter_uuid === submitter.uuid ? '#eef2ff' : '#fff',
                  transition: 'all 0.12s'
                }"
              >
                <span :style="{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  flexShrink: '0',
                  background: submitterColors[index % submitterColors.length]
                }"></span>
                <span style="font-size:13px;font-weight:500;color:#152C54;flex:1;">{{ submitter.name }}</span>
                <svg v-if="selectedPanelField.submitter_uuid === submitter.uuid" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--brand, #1E3A6E)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
            </div>
          </div>
          <ul class="menu menu-xs p-0 space-y-0.5">
            <FieldSettings
              :field="selectedPanelField"
              :editable="editable"
              :with-signature-id="withSignatureId"
              :with-prefillable="withPrefillable"
              :background-color="backgroundColor"
              :with-areas="true"
              :with-required="true"
              @click-formula="selectedFormulaField = selectedPanelField"
              @click-font="selectedFontField = selectedPanelField"
              @click-description="selectedDescriptionField = selectedPanelField"
              @click-condition="startConditionPick"
              @set-draw="[$emit('set-draw', $event)]"
              @remove-area="removeArea($event)"
              @scroll-to="scrollToArea($event)"
            />
          </ul>
          <div v-if="isConditionPickMode" style="margin-top:10px;padding:12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style="font-size:11px;font-weight:600;color:#1d4ed8;">Click a field on the document to use as condition source</span>
            </div>
            <button style="width:100%;padding:5px;border:1px solid #bfdbfe;border-radius:6px;background:#fff;cursor:pointer;font-size:11px;color:#3b82f6;font-weight:500;display:inline-flex;align-items:center;justify-content:center;gap:5px;" @click="cancelConditionPick"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>Cancel</button>
          </div>
          <div v-else-if="conditionPickSourceField" style="margin-top:10px;padding:12px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;">
            <div style="font-size:10px;font-weight:700;color:#15803d;letter-spacing:.4px;text-transform:uppercase;margin-bottom:8px;">Condition Source: {{ conditionPickSourceField.name || buildDefaultFieldName(conditionPickSourceField, template.fields) }}</div>
            <select class="select select-bordered select-xs w-full font-normal" style="margin-bottom:6px;" v-model="conditionPickAction">
              <option v-for="action in getConditionActions(conditionPickSourceField)" :key="action" :value="action">{{ action }}</option>
            </select>
            <select v-if="['radio', 'select', 'multiple'].includes(conditionPickSourceField.type) && conditionPickSourceField.options" class="select select-bordered select-xs w-full font-normal" style="margin-bottom:6px;" v-model="conditionPickValue">
              <option value="" disabled>Select value...</option>
              <option v-for="(option, index) in conditionPickSourceField.options" :key="option.uuid" :value="option.uuid">{{ option.value || 'Option ' + (index + 1) }}</option>
            </select>
            <div style="display:flex;gap:6px;margin-top:4px;">
              <button style="flex:1;padding:5px;border:none;border-radius:6px;background:#16a34a;color:#fff;cursor:pointer;font-size:11px;font-weight:600;" @click="saveConditionPick">Save</button>
              <button style="flex:1;padding:5px;border:1px solid #d1d5db;border-radius:6px;background:#fff;color:#374151;cursor:pointer;font-size:11px;font-weight:500;" @click="cancelConditionPick">Cancel</button>
            </div>
          </div>
          <div v-else-if="selectedPanelField && selectedPanelField.conditions && selectedPanelField.conditions.length" style="margin-top:10px;padding:10px 12px;background:#fefce8;border:1px solid #fde047;border-radius:8px;text-align:center;">
            <div style="font-size:11px;font-weight:600;color:#854d0e;margin-bottom:4px;display:inline-flex;align-items:center;justify-content:center;gap:5px;"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5l10 -10"/></svg>Condition is set</div>
            <button style="font-size:11px;color:#ef4444;background:none;border:none;cursor:pointer;text-decoration:underline;" @click="removeCondition">Remove Condition</button>
          </div>
          <div class="mt-3 border-t border-base-300 pt-3">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.6px;text-transform:uppercase;margin-bottom:5px;">Data Label</div>
            <input
              class="input input-bordered input-xs w-full bg-transparent"
              :value="selectedPanelField.name || (fieldPanelTitle + ' ' + selectedPanelField.uuid.split('-')[0])"
              placeholder="Data label..."
              @blur="updateFieldName($event.target.value)"
              @keydown.enter.prevent="$event.target.blur()"
            />
            <div style="font-size:10px;color:#9ca3af;margin-top:3px;">Used to identify field data on submission</div>
          </div>
          <div style="margin-top:14px;padding-top:10px;border-top:1px solid #f1f5f9;display:flex;justify-content:center;">
            <button
              style="display:flex;align-items:center;gap:5px;padding:5px 12px;border:none;background:none;cursor:pointer;font-size:12px;color:#ef4444;font-weight:500;border-radius:6px;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#fef2f2'"
              @mouseleave="$event.currentTarget.style.background='none'"
              @click="removeFieldByArea(selectedAreaRef.value)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              Delete Field
            </button>
          </div>
        </div>
        <div v-else-if="alignmentFieldUuids.value && alignmentFieldUuids.value.size >= 2" class="px-1">
          <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.6px;text-transform:uppercase;margin-bottom:8px;">Align Fields</div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;">
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('left')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
              Left
            </button>
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('center')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
              Center
            </button>
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('right')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
              Right
            </button>
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('top')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="6" y1="3" x2="18" y2="3"/><line x1="12" y1="3" x2="12" y2="21"/><polyline points="8 7 12 3 16 7"/></svg>
              Top
            </button>
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('middle')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="21"/></svg>
              Middle
            </button>
            <button style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:7px 4px;border:1px solid #e2e8f0;border-radius:7px;background:#fff;cursor:pointer;font-size:10px;font-weight:500;color:#374151;transition:background 0.15s;"
              @mouseenter="$event.currentTarget.style.background='#eff6ff'"
              @mouseleave="$event.currentTarget.style.background='#fff'"
              @click="$refs.fields.alignFields('bottom')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><line x1="6" y1="21" x2="18" y2="21"/><line x1="12" y1="3" x2="12" y2="21"/><polyline points="8 17 12 21 16 17"/></svg>
              Bottom
            </button>
          </div>
        </div>
        <div v-else-if="selectedFieldUuids.value && selectedFieldUuids.value.size > 0" class="px-1">
          <div style="font-size:11px;font-weight:600;color:#7c3aed;margin-bottom:8px;display:flex;align-items:center;gap:5px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            {{ selectedFieldUuids.value.size }} field{{ selectedFieldUuids.value.size > 1 ? 's' : '' }} selected
          </div>
          <!-- Recipient first: reassigning who fills a field is a bigger change than any of the
               formatting controls below it, and DocuSign puts it at the top of the same panel.
               Rendered as the same colour-dot list the single-field RECIPIENT picker uses rather
               than a <select>: the dot is the only thing that ties a row here to the hue the
               field boxes are painted in, and a collapsed select hid it. Reads/writes
               submitter_uuid through $refs.fields, the same way every other control in this
               panel reaches its setter.

               bulkSubmitterUuid() is a method call, not a computed: it reads $refs.fields, which
               is null on the first render of this branch. `mountedRefs` is flipped in mounted()
               so the list re-renders once the ref exists -- without it the first paint after a
               selection shows every row unticked. -->
          <div style="margin-bottom:12px;">
            <div style="font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:.8px;text-transform:uppercase;margin-bottom:6px;">Assign to recipient</div>
            <div style="display:flex;flex-direction:column;gap:4px;">
              <div
                v-for="(submitter, index) in template.submitters"
                :key="submitter.uuid"
                @click="$refs.fields.setBulkSubmitter(submitter.uuid)"
                :style="{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 10px',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  border: bulkSubmitterUuid === submitter.uuid ? '1.5px solid var(--brand, #1E3A6E)' : '1.5px solid #e5e7eb',
                  background: bulkSubmitterUuid === submitter.uuid ? '#eef2ff' : '#fff',
                  transition: 'all 0.12s'
                }"
              >
                <span :style="{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  flexShrink: '0',
                  background: submitterColors[index % submitterColors.length]
                }"></span>
                <span style="font-size:13px;font-weight:500;color:#152C54;flex:1;">{{ submitter.name }}</span>
                <svg v-if="bulkSubmitterUuid === submitter.uuid" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--brand, #1E3A6E)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
            </div>
            <!-- Shown only while the selection spans recipients. States what is selected rather
                 than offering it as a choice: there is no "mixed" to assign back to. -->
            <div
              v-if="mountedRefs && !bulkSubmitterUuid"
              style="font-size:10px;line-height:1.45;color:#64748b;margin-top:6px;padding:0 2px;"
            >
              These fields belong to more than one recipient. Pick one above to move them all.
            </div>
          </div>
          <div class="flex flex-wrap gap-3 mb-2">
            <label class="flex items-center gap-1.5 cursor-pointer text-xs font-medium">
              <input type="checkbox" class="checkbox checkbox-xs checkbox-primary" :checked="$refs.fields && $refs.fields.bulkAllRequired()" @change="$refs.fields.setBulkProp('required', $event.target.checked)" />
              Required
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer text-xs font-medium">
              <input type="checkbox" class="checkbox checkbox-xs" :checked="$refs.fields && $refs.fields.bulkAllReadonly()" @change="$refs.fields.setBulkProp('readonly', $event.target.checked)" />
              Read Only
            </label>
          </div>
          <div class="space-y-2 mb-2">
            <div class="relative">
              <select class="select select-bordered select-xs w-full font-normal" @change="$refs.fields.setBulkValidation($event.target.value)">
                <option value="none">None</option>
                <option value="email">Email</option>
                <option value="url">URL</option>
                <option value="numbers">Numbers only</option>
                <option value="letters">Letters only</option>
                <option value="zip">ZIP</option>
                <option value="ssn">SSN</option>
                <option value="ein">EIN</option>
              </select>
              <label class="absolute -top-1.5 left-2 px-1 bg-white text-purple-600" style="font-size:8px">Validation</label>
            </div>
            <div class="flex gap-2">
              <div class="relative flex-1">
                <select class="select select-bordered select-xs w-full font-normal" @change="$refs.fields.setBulkFont($event.target.value)">
                  <option value="">Default</option>
                  <option value="Times">Times New Roman</option>
                  <option value="Courier">Courier New</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Arial">Arial</option>
                  <option value="Tahoma">Tahoma</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Trebuchet">Trebuchet MS</option>
                  <option value="Calibri">Calibri</option>
                </select>
                <label class="absolute -top-1.5 left-2 px-1 bg-white text-purple-600" style="font-size:8px">Font</label>
              </div>
              <div class="relative w-24">
                <select class="select select-bordered select-xs w-full font-normal" @change="$refs.fields.setBulkFontSize($event.target.value)">
                  <option value="">Auto</option>
                  <option value="8">8</option>
                  <option value="10">10</option>
                  <option value="12">12</option>
                  <option value="14">14</option>
                  <option value="16">16</option>
                  <option value="18">18</option>
                  <option value="24">24</option>
                </select>
                <label class="absolute -top-1.5 left-2 px-1 bg-white text-purple-600" style="font-size:8px">Size</label>
              </div>
            </div>
          </div>
          <div v-if="isBulkConditionPickMode" style="margin-top:8px;padding:12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style="font-size:11px;font-weight:600;color:#1d4ed8;">Click a field on the document to use as condition source</span>
            </div>
            <button style="width:100%;padding:5px;border:1px solid #bfdbfe;border-radius:6px;background:#fff;cursor:pointer;font-size:11px;color:#3b82f6;font-weight:500;display:inline-flex;align-items:center;justify-content:center;gap:5px;" @click="cancelBulkConditionPick"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>Cancel</button>
          </div>
          <div v-else-if="bulkConditionPickSourceField" style="margin-top:8px;padding:12px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;">
            <div style="font-size:10px;font-weight:700;color:#15803d;letter-spacing:.4px;text-transform:uppercase;margin-bottom:8px;">Condition Source: {{ bulkConditionPickSourceField.name || buildDefaultFieldName(bulkConditionPickSourceField, template.fields) }}</div>
            <select class="select select-bordered select-xs w-full font-normal" style="margin-bottom:6px;" v-model="bulkConditionPickAction">
              <option v-for="action in getConditionActions(bulkConditionPickSourceField)" :key="action" :value="action">{{ action }}</option>
            </select>
            <select v-if="['radio', 'select', 'multiple'].includes(bulkConditionPickSourceField.type) && bulkConditionPickSourceField.options" class="select select-bordered select-xs w-full font-normal" style="margin-bottom:6px;" v-model="bulkConditionPickValue">
              <option value="" disabled>Select value...</option>
              <option v-for="(option, index) in bulkConditionPickSourceField.options" :key="option.uuid" :value="option.uuid">{{ option.value || 'Option ' + (index + 1) }}</option>
            </select>
            <div style="display:flex;gap:6px;">
              <button style="flex:1;padding:5px;border:none;border-radius:6px;background:#16a34a;color:#fff;cursor:pointer;font-size:11px;font-weight:600;" @click="saveBulkConditionPick">Save</button>
              <button style="flex:1;padding:5px;border:1px solid #d1d5db;border-radius:6px;background:#fff;color:#374151;cursor:pointer;font-size:11px;font-weight:500;" @click="cancelBulkConditionPick">Cancel</button>
            </div>
          </div>
          <button v-else style="display:flex;align-items:center;justify-content:center;gap:5px;width:100%;padding:6px 12px;border:1.5px dashed #c4b5fd;border-radius:6px;background:#faf5ff;cursor:pointer;font-size:11px;font-weight:600;color:#7c3aed;margin-top:4px;transition:background 0.15s;"
            @mouseenter="$event.currentTarget.style.background='#f3e8ff'"
            @mouseleave="$event.currentTarget.style.background='#faf5ff'"
            @click="startBulkConditionPick">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            Set Condition
          </button>
          <button style="display:flex;align-items:center;justify-content:center;gap:5px;width:100%;padding:6px 12px;border:1.5px dashed #fca5a5;border-radius:6px;background:#fef2f2;cursor:pointer;font-size:11px;font-weight:600;color:#dc2626;margin-top:6px;transition:background 0.15s;"
            title="Delete selected fields (Del)"
            @mouseenter="$event.currentTarget.style.background='#fee2e2'"
            @mouseleave="$event.currentTarget.style.background='#fef2f2'"
            @click="removeSelectedFields">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            Delete {{ selectedFieldUuids.value.size }} field{{ selectedFieldUuids.value.size > 1 ? 's' : '' }}
          </button>
        </div>
        <div v-else-if="isPageConditionPickMode" class="mt-2 p-3 bg-blue-50 border border-blue-300 rounded-lg">
          <div class="text-xs text-blue-700 font-semibold mb-1">Page: {{ pageConditionItem?.name }}</div>
          <div class="text-xs text-blue-600 mb-2">Click a field on the document to use as condition source</div>
          <button class="btn btn-xs btn-ghost text-blue-600 w-full gap-1" @click="cancelPageConditionPick"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>Cancel</button>
        </div>
        <div v-else-if="pageConditionSourceField" class="mt-2 p-3 bg-green-50 border border-green-300 rounded-lg space-y-2">
          <div class="text-xs font-semibold text-green-800">Condition Source: {{ pageConditionSourceField.name || buildDefaultFieldName(pageConditionSourceField, template.fields) }}</div>
          <select class="select select-bordered select-xs w-full font-normal" v-model="pageConditionAction">
            <option v-for="action in getConditionActions(pageConditionSourceField)" :key="action" :value="action">{{ action }}</option>
          </select>
          <select v-if="['radio', 'select', 'multiple'].includes(pageConditionSourceField.type) && pageConditionSourceField.options" class="select select-bordered select-xs w-full font-normal" v-model="pageConditionValue">
            <option value="" disabled>Select value...</option>
            <option v-for="(option, index) in pageConditionSourceField.options" :key="option.uuid" :value="option.uuid">{{ option.value || 'Option ' + (index + 1) }}</option>
          </select>
          <div class="flex gap-2">
            <button class="btn btn-xs btn-success flex-1" @click="savePageConditionPick">Save</button>
            <button class="btn btn-xs btn-ghost flex-1" @click="cancelPageConditionPick">Cancel</button>
          </div>
          <div v-if="pageConditionItem && pageConditionItem.conditions && pageConditionItem.conditions.length" class="text-center">
            <button class="link text-xs text-red-500" @click="removePageCondition">Remove Condition</button>
          </div>
        </div>
        <div v-else-if="!isPreviewCollapsed">
        <DocumentPreview
          v-for="(item, index) in template.schema"
          :key="index"
          :with-arrows="template.schema.length > 1"
          :item="item"
          :document="sortedDocuments[index]"
          :data-document-uuid="item.attachment_uuid"
          :accept-file-types="acceptFileTypes"
          :with-replace-button="withUploadButton"
          :editable="editable"
          :template="template"
          @scroll-to="scrollIntoDocument(item)"
          @remove="onDocumentRemove"
          @replace="onDocumentReplace"
          @up="moveDocument(item, -1)"
          @reorder="reorderFields"
          @down="moveDocument(item, 1)"
          @change="save"
          @set-page-condition="startPageConditionPick($event)"
        />
        <div
          class="sticky bottom-0 py-2 space-y-2"
          :style="{ backgroundColor }"
        >
          <Upload
            v-if="editable && withUploadButton"
            v-show="sortedDocuments.length"
            ref="upload"
            :accept-file-types="acceptFileTypes"
            :authenticity-token="authenticityToken"
            :with-google-drive="withGoogleDrive"
            :template-id="template.id"
            @success="updateFromUpload"
          />
          <button
            v-if="sortedDocuments.length && editable && withAddPageButton"
            id="add_blank_page_button"
            class="btn btn-outline w-full add-blank-page-button"
            @click.prevent="addBlankPage"
          >
            <IconInnerShadowTop
              v-if="isLoadingBlankPage"
              class="animate-spin w-5 h-5"
            />
            <IconPlus
              v-else
              class="w-5 h-5"
            />
            {{ t('add_blank_page') }}
          </button>
        </div>
        </div>
      </div>
    </div>
    <div class="sticky bottom-0 z-10">
      <MobileDrawField
        v-if="drawField && (isBreakpointLg || isMobile)"
        :draw-field="drawField"
        :fields="template.fields"
        :submitters="template.submitters"
        :selected-submitter="selectedSubmitter"
        :class="{ 'md:hidden': !isMobile }"
        :editable="editable"
        @cancel="[drawField = null, drawOption = null]"
        @change-submitter="[selectedSubmitter = $event, drawField.submitter_uuid = $event.uuid]"
      />
      <MobileFields
        v-if="sortedDocuments.length && !drawField && editable"
        :fields="template.fields"
        :default-fields="[...defaultRequiredFields, ...defaultFields]"
        :default-required-fields="defaultRequiredFields"
        :field-types="fieldTypes"
        :class="{ 'md:hidden': !isMobile }"
        :selected-submitter="selectedSubmitter"
        @select="startFieldDraw($event)"
      />
    </div>
    <div
      id="docuseal_modal_container"
      class="modal-container"
    />
    <div v-if="isShortcutsModal" class="modal modal-open items-start !animate-none overflow-y-auto">
      <div class="absolute top-0 bottom-0 right-0 left-0" @click="isShortcutsModal = false" />
      <div class="modal-box pt-4 pb-6 px-6 mt-20 max-h-none w-full max-w-xl">
        <div class="flex justify-between items-center border-b pb-2 mb-4 font-medium">
          <span class="text-lg font-semibold">Keyboard Shortcuts</span>
          <a href="#" class="text-xl" @click.prevent="isShortcutsModal = false">&times;</a>
        </div>
        <div class="space-y-4">
          <div>
            <div class="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">Fields</div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="flex justify-between"><span>Delete Field</span><kbd class="kbd kbd-sm">Delete</kbd></div>
              <div class="flex justify-between"><span>Copy Field</span><kbd class="kbd kbd-sm">Ctrl+C</kbd></div>
              <div class="flex justify-between"><span>Paste Field</span><kbd class="kbd kbd-sm">Ctrl+V</kbd></div>
              <div class="flex justify-between"><span>Deselect</span><kbd class="kbd kbd-sm">Escape</kbd></div>
              <div class="flex justify-between"><span>Move Field</span><kbd class="kbd kbd-sm">Arrow Keys</kbd></div>
              <div class="flex justify-between"><span>Move Field (large)</span><kbd class="kbd kbd-sm">Shift+Arrow</kbd></div>
            </div>
          </div>
          <div>
            <div class="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">Selection</div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="flex justify-between"><span>Select Field (Bulk)</span><kbd class="kbd kbd-sm">Ctrl+Click</kbd></div>
              <div class="flex justify-between"><span>Select Multiple</span><kbd class="kbd kbd-sm">Ctrl+Drag</kbd></div>
              <div class="flex justify-between"><span>Align Fields</span><kbd class="kbd kbd-sm">Shift+Drag</kbd></div>
            </div>
          </div>
          <div>
            <div class="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">Common Actions</div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="flex justify-between"><span>Undo</span><kbd class="kbd kbd-sm">Ctrl+Z</kbd></div>
              <div class="flex justify-between"><span>Redo</span><kbd class="kbd kbd-sm">Ctrl+Shift+Z</kbd></div>
              <div class="flex justify-between"><span>Save</span><kbd class="kbd kbd-sm">Ctrl+S</kbd></div>
            </div>
          </div>
        </div>
        <div class="mt-6">
          <button class="base-button w-full" @click="isShortcutsModal = false">OK</button>
        </div>
      </div>
    </div>
    <Teleport v-if="selectedFormulaField" to="#docuseal_modal_container">
      <FormulaModal
        :field="selectedFormulaField"
        :editable="editable"
        :build-default-name="() => {}"
        @close="selectedFormulaField = null"
      />
    </Teleport>
    <Teleport v-if="selectedFontField" to="#docuseal_modal_container">
      <FontModal
        :field="selectedFontField"
        :editable="editable"
        :build-default-name="() => {}"
        @close="selectedFontField = null"
      />
    </Teleport>
    <Teleport v-if="selectedConditionField" to="#docuseal_modal_container">
      <ConditionsModal
        :item="selectedConditionField"
        :build-default-name="buildDefaultFieldName"
        @close="selectedConditionField = null"
      />
    </Teleport>
    <Teleport v-if="selectedDescriptionField" to="#docuseal_modal_container">
      <DescriptionModal
        :field="selectedDescriptionField"
        :editable="editable"
        :build-default-name="() => {}"
        @close="selectedDescriptionField = null"
      />
    </Teleport>
    <!-- Edit Message Modal -->
    <div v-if="isEditMessageModal" style="position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;">
      <div style="background:#fff;border-radius:8px;width:560px;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <h2 style="font-size:18px;font-weight:600;color:#1a1a2e;margin:0 0 16px;">Edit Message</h2>
        <!-- Saved wording from /settings/message_templates. Picking one fills the two inputs
             below; nothing records which one was picked, so the select returns to its
             placeholder rather than asserting a link the data does not hold. -->
        <div style="margin-bottom:14px;">
          <label style="display:block;font-size:12px;font-weight:700;color:#6B7280;margin-bottom:6px;letter-spacing:.4px;">USE A SAVED MESSAGE</label>
          <select
            :disabled="messageTemplates.length === 0"
            style="width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;font-family:inherit;background:#fff;cursor:pointer;"
            @change="applyMessageTemplate($event)"
          >
            <option value="">{{ messageTemplates.length ? 'Select a saved message…' : 'No saved messages yet' }}</option>
            <option
              v-for="messageTemplate in messageTemplates"
              :key="messageTemplate.id"
              :value="messageTemplate.id"
            >{{ messageTemplate.name }}</option>
          </select>
        </div>
        <div style="margin-bottom:14px;">
          <label style="display:block;font-size:12px;font-weight:700;color:#6B7280;margin-bottom:6px;letter-spacing:.4px;">EMAIL SUBJECT</label>
          <input v-model="envelopeEditMessage.subject" type="text" placeholder="Please sign this document" style="width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;font-family:inherit;">
        </div>
        <div style="margin-bottom:20px;">
          <label style="display:block;font-size:12px;font-weight:700;color:#6B7280;margin-bottom:6px;letter-spacing:.4px;">MESSAGE</label>
          <textarea v-model="envelopeEditMessage.body" rows="4" placeholder="Please review and sign the attached document." style="width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;font-family:inherit;resize:vertical;"></textarea>
        </div>
        <!-- Files whatever is typed above for reuse. Separate from Save below, which writes the
             wording onto THIS envelope: saving one has never implied the other. -->
        <div style="display:flex;gap:8px;align-items:flex-end;margin-bottom:20px;padding:12px;border:1px solid #E5E7EB;border-radius:8px;background:#FAFAFC;">
          <div style="flex:1;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;letter-spacing:.4px;">SAVE THIS WORDING AS</label>
            <input v-model="messageTemplateName" type="text" placeholder="e.g. Offer letter" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;">
          </div>
          <button
            :disabled="messageTemplateSaving || !messageTemplateName.trim()"
            :style="{ padding: '8px 14px', border: '1px solid #D1D5DB', borderRadius: '6px', background: '#fff', cursor: messageTemplateSaving || !messageTemplateName.trim() ? 'default' : 'pointer', fontSize: '13px', fontFamily: 'inherit', fontWeight: '600', opacity: messageTemplateSaving || !messageTemplateName.trim() ? '.5' : '1', flexShrink: '0' }"
            @click="saveMessageTemplateFromModal"
          >{{ messageTemplateSaving ? 'Saving…' : 'Save as template' }}</button>
        </div>
        <div v-if="messageTemplateError" style="font-size:12px;color:#ef4444;margin:-12px 0 16px;">{{ messageTemplateError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:10px;">
          <button @click="isEditMessageModal = false" style="padding:9px 18px;border:1px solid #D1D5DB;border-radius:6px;background:#fff;cursor:pointer;font-size:13px;font-family:inherit;">Cancel</button>
          <button @click="saveEditMessage" style="padding:9px 18px;border:none;border-radius:6px;background:var(--brand, #1E3A6E);color:#fff;cursor:pointer;font-size:13px;font-weight:600;font-family:inherit;">Save</button>
        </div>
      </div>
    </div>

    <!-- Edit Recipients Modal -->
    <div v-if="isEditRecipientsModal" style="position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;">
      <div style="background:#fff;border-radius:8px;width:620px;max-height:80vh;overflow-y:auto;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <h2 style="font-size:18px;font-weight:600;color:#1a1a2e;margin:0 0 6px;">Edit Recipients</h2>
        <!-- Same routing-number model as the Signers panel and the envelope creator: the number
             decides who is invited when, and two recipients sharing a number are invited
             together. Unticking clears every number, which is what makes the send parallel
             (Envelopes::SendSubmission treats "no non-zero signing_order" as unordered). -->
        <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;width:fit-content;margin:0 0 16px;">
          <input
            type="checkbox"
            :checked="envelopeEditSigningOrder"
            style="width:15px;height:15px;margin-top:2px;cursor:pointer;accent-color:var(--brand, #1E3A6E);"
            @change="toggleEditRecipientsOrder($event.target.checked)"
          >
          <span>
            <span style="display:block;font-size:13px;font-weight:600;color:#1a1a2e;">Set signing order</span>
            <span style="display:block;font-size:12px;color:#6B7280;margin-top:2px;">Recipients are invited in this order. Give two recipients the same number to invite them at the same time.</span>
          </span>
        </label>
        <div v-for="(r, i) in envelopeEditRecipients" :key="r.id" style="display:flex;gap:10px;margin-bottom:12px;align-items:flex-end;">
          <div v-if="envelopeEditSigningOrder" style="flex-shrink:0;width:62px;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;">ORDER</label>
            <input v-model.number="r.signing_order" type="number" min="1" :max="envelopeEditRecipients.length" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;text-align:center;">
          </div>
          <div style="flex:1;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;">ROLE</label>
            <input v-model="r.role_name" type="text" placeholder="e.g. Employee" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;">
          </div>
          <div style="flex:1;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;">NAME</label>
            <input v-model="r.name" type="text" placeholder="Full name" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;">
          </div>
          <div style="flex:1;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;">EMAIL</label>
            <input v-model="r.email" type="email" placeholder="email@example.com" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;">
          </div>
          <div style="flex-shrink:0;">
            <label style="display:block;font-size:11px;font-weight:700;color:#6B7280;margin-bottom:4px;">ACTION</label>
            <select v-model="r.role" style="padding:8px 10px;border:1px solid #D1D5DB;border-radius:6px;font-size:13px;font-family:inherit;background:#fff;">
              <option value="signer">Needs to Sign</option>
              <option value="cc">Receives a Copy</option>
              <option value="viewer">Needs to View</option>
            </select>
          </div>
        </div>
        <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:8px;">
          <button @click="isEditRecipientsModal = false" style="padding:9px 18px;border:1px solid #D1D5DB;border-radius:6px;background:#fff;cursor:pointer;font-size:13px;font-family:inherit;">Cancel</button>
          <button @click="saveEditRecipients" style="padding:9px 18px;border:none;border-radius:6px;background:var(--brand, #1E3A6E);color:#fff;cursor:pointer;font-size:13px;font-weight:600;font-family:inherit;">Save</button>
        </div>
      </div>
    </div>

    <div v-if="isAdvancedOptionsModal" style="position:fixed;inset:0;z-index:10000;background:#fff;display:flex;flex-direction:column;">
      <!-- Top bar -->
      <div style="display:flex;align-items:center;justify-content:space-between;padding:0 24px;height:56px;border-bottom:1px solid #E5E7EB;flex-shrink:0;">
        <h1 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0;">Advanced Options</h1>
        <button style="background:none;border:none;cursor:pointer;color:#6B7280;display:inline-flex;" @click="isAdvancedOptionsModal = false"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg></button>
      </div>
      <!-- Body -->
      <div style="display:flex;flex:1;overflow:hidden;">
        <!-- Left nav -->
        <nav style="width:200px;border-right:1px solid #E5E7EB;padding:16px 0;flex-shrink:0;">
          <div v-for="sec in advSections" :key="sec.id"
            @click="advActiveSection = sec.id; $nextTick(() => { const el = document.getElementById('adv-edit-' + sec.id); if(el) el.scrollIntoView({behavior:'smooth',block:'start'}) })"
            :style="`padding:10px 20px;font-size:14px;cursor:pointer;color:${advActiveSection === sec.id ? 'var(--brand, #1E3A6E)' : '#374151'};font-weight:${advActiveSection === sec.id ? '600' : '400'};border-left:${advActiveSection === sec.id ? '3px solid var(--brand, #1E3A6E)' : '3px solid transparent'}`">
            {{ sec.label }}
          </div>
        </nav>
        <!-- Content -->
        <div style="flex:1;overflow-y:auto;">
          <!-- Recipient Privileges -->
          <div id="adv-edit-recipient-privileges" style="padding:32px 40px;border-bottom:1px solid #E5E7EB;">
            <h2 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px;">Recipient Privileges</h2>
            <p style="font-size:14px;color:#4B5563;margin:0 0 20px;">Give recipients options for how they sign.</p>
            <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
              <input type="checkbox" v-model="advEdit.can_delegate" style="width:16px;height:16px;accent-color:var(--brand, #1E3A6E);">
              <span style="font-size:14px;color:#1a1a2e;">Recipients can change signing responsibility or assign a delegate</span>
            </label>
          </div>
          <!-- Reminders -->
          <div id="adv-edit-reminders" style="padding:32px 40px;border-bottom:1px solid #E5E7EB;">
            <h2 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px;">Reminders</h2>
            <p style="font-size:14px;color:#4B5563;margin:0 0 20px;">Follow up with automatic reminders. Signers will receive emails until they sign or decline the envelope.</p>
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px;">
              <div @click="advEdit.reminder_enabled = !advEdit.reminder_enabled"
                :style="`position:relative;display:inline-flex;align-items:center;width:44px;height:24px;border-radius:12px;cursor:pointer;transition:.2s;background:${advEdit.reminder_enabled ? 'var(--brand, #1E3A6E)' : '#D1D5DB'};flex-shrink:0`">
                <span :style="`position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;transition:.2s;left:${advEdit.reminder_enabled ? '23px' : '3px'};box-shadow:0 1px 3px rgba(0,0,0,.3)`"></span>
              </div>
              <span style="font-size:14px;color:#374151;">Turn on auto reminders</span>
            </div>
            <div v-if="advEdit.reminder_enabled" style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Send first reminder</div>
                <select v-model="advEdit.reminder_days" style="width:100%;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;background:#fff;">
                  <option v-for="d in [1,2,3,5,7,10,14]" :key="d" :value="d">{{ d }} day{{ d > 1 ? 's' : '' }} after sending</option>
                </select>
              </div>
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Send reminder every</div>
                <select v-model="advEdit.reminder_frequency" style="width:100%;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;background:#fff;">
                  <option v-for="d in [1,2,3,5,7]" :key="d" :value="d">{{ d }} day{{ d > 1 ? 's' : '' }}</option>
                </select>
              </div>
            </div>
          </div>
          <!-- Expiration -->
          <div id="adv-edit-expiration" style="padding:32px 40px;border-bottom:1px solid #E5E7EB;">
            <h2 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px;">Expiration</h2>
            <p style="font-size:14px;color:#4B5563;margin:0 0 20px;">By default, envelopes expire after 120 days. Recipients can no longer view or sign an envelope after it expires.</p>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end;">
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Days until envelope expires</div>
                <select v-model="advEdit.expire_after_days" style="width:100%;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;background:#fff;">
                  <option v-for="d in [7,14,30,60,90,120,180,365]" :key="d" :value="d">{{ d }} days</option>
                </select>
              </div>
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Custom number of days <span style="color:#DC2626">*</span></div>
                <input type="number" v-model.number="advEdit.expire_after_days" min="1" max="999" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;">
              </div>
            </div>
            <div style="margin-top:8px;font-size:13px;color:#374151;">Envelopes will expire on: <strong>{{ new Date(Date.now() + (advEdit.expire_after_days || 120) * 86400000).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: 'numeric'}) }}</strong></div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end;margin-top:20px;">
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Send alert before expiry</div>
                <select v-model.number="advEdit.expiration_alert_days" style="width:100%;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;background:#fff;">
                  <option :value="0">No alert</option>
                  <option :value="1">1 day before</option>
                  <option :value="3">3 days before</option>
                  <option :value="7">7 days before</option>
                  <option :value="14">14 days before</option>
                </select>
              </div>
              <div>
                <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Custom number of days <span style="color:#DC2626">*</span></div>
                <input type="number" v-model.number="advEdit.expiration_alert_days" min="0" max="999" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid #D1D5DB;border-radius:4px;font-size:13px;">
              </div>
            </div>
          </div>
          <!-- Template Password -->
          <div id="adv-edit-template-password" style="padding:32px 40px;border-bottom:1px solid #E5E7EB;">
            <h2 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px;">Template Password</h2>
            <p style="font-size:14px;color:#4B5563;margin:0 0 16px;">Use a password to protect this template from being edited.</p>
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px;">
              <div @click="advEdit.password_enabled = !advEdit.password_enabled"
                :style="`position:relative;display:inline-flex;align-items:center;width:44px;height:24px;border-radius:12px;cursor:pointer;transition:.2s;background:${advEdit.password_enabled ? 'var(--brand, #1E3A6E)' : '#D1D5DB'};flex-shrink:0`">
                <span :style="`position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;transition:.2s;left:${advEdit.password_enabled ? '23px' : '3px'};box-shadow:0 1px 3px rgba(0,0,0,.3)`"></span>
              </div>
              <span style="font-size:14px;color:#374151;">Turn on password protection</span>
            </div>
            <div v-if="advEdit.password_enabled" style="max-width:320px;">
              <div style="font-size:13px;font-weight:500;color:#374151;margin-bottom:4px;">Password</div>
              <input type="password" v-model="advEdit.password" placeholder="Enter password" style="width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;">
            </div>
          </div>
          <!-- Template Modification -->
          <div id="adv-edit-template-modification" style="padding:32px 40px;">
            <h2 style="font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px;">Template Modification</h2>
            <p style="font-size:14px;color:#4B5563;margin:0 0 20px;">Limit the changes someone can make when they use this template.</p>
            <label style="display:flex;align-items:center;gap:10px;margin-bottom:12px;cursor:pointer;">
              <input type="checkbox" v-model="advEdit.allow_recipients_edit" style="width:16px;height:16px;accent-color:var(--brand, #1E3A6E);">
              <span style="font-size:14px;color:#1a1a2e;">Allow senders to edit, add, or remove recipients</span>
            </label>
            <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
              <input type="checkbox" v-model="advEdit.allow_message_edit" style="width:16px;height:16px;accent-color:var(--brand, #1E3A6E);">
              <span style="font-size:14px;color:#1a1a2e;">Allow senders to edit the subject, email, or private messages</span>
            </label>
          </div>
        </div>
      </div>
      <!-- Save bar -->
      <div style="border-top:1px solid #E5E7EB;padding:14px 24px;display:flex;justify-content:flex-end;flex-shrink:0;background:#fff;">
        <button @click="saveAdvancedOptions"
          :disabled="advSaving"
          style="padding:10px 32px;background:var(--brand, #1E3A6E);color:#fff;border:none;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;">
          {{ advSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Upload from './upload'
import Dropzone from './dropzone'
import HoverDropzone from './hover_dropzone'
import DragPlaceholder from './drag_placeholder'
import Fields from './fields'
import MobileDrawField from './mobile_draw_field'
import Document from './document'
import Logo from './logo'
import Contenteditable from './contenteditable'
import DocumentPreview from './preview'
import DocumentControls from './controls'
import MobileFields from './mobile_fields'
import FieldSubmitter from './field_submitter'
import { ROLE_DEFAULTS } from './signer_roles'
import { showConfirm } from '../dialog'
import FieldSettings from './field_settings'
import FieldType from './field_type'
import FormulaModal from './formula_modal'
import FontModal from './font_modal'
import ConditionsModal from './conditions_modal'
import DescriptionModal from './description_modal'
import { IconPlus, IconMinus, IconUsersPlus, IconDeviceFloppy, IconChevronDown, IconEye, IconWritingSign, IconInnerShadowTop, IconInfoCircle, IconAdjustments, IconDownload, IconArrowLeft, IconAlertTriangle, IconTrashX } from '@tabler/icons-vue'
import { v4 } from 'uuid'
import { loadMessageTemplates, saveMessageTemplate } from './message_templates'
import { ref, computed, toRaw } from 'vue'
import * as i18n from './i18n'

// Mirrors EnvelopeRecipient's `role` enum and the wording the Envelope creator already uses for
// the same values (EnvelopeCreator.jsx:346 and :907) -- change one and change the other, or the
// builder will name an action type differently from the screen that sets it.
const RECIPIENT_ROLE_LABELS = {
  signer: 'Needs to Sign',
  cc: 'Receives a Copy',
  viewer: 'Needs to View'
}

export default {
  name: 'TemplateBuilder',
  components: {
    Upload,
    IconArrowLeft,
    IconAlertTriangle,
    IconTrashX,
    DragPlaceholder,
    Document,
    Fields,
    IconInfoCircle,
    MobileDrawField,
    IconPlus,
    IconMinus,
    IconWritingSign,
    MobileFields,
    Logo,
    Dropzone,
    HoverDropzone,
    DocumentPreview,
    DocumentControls,
    IconInnerShadowTop,
    Contenteditable,
    IconUsersPlus,
    IconChevronDown,
    IconDownload,
    IconAdjustments,
    IconEye,
    IconDeviceFloppy,
    FieldSettings,
    FormulaModal,
    FontModal,
    ConditionsModal,
    DescriptionModal
  },
  provide () {
    return {
      template: this.template,
      save: this.save,
      t: this.t,
      assignDropAreaSize: this.assignDropAreaSize,
      currencies: this.currencies,
      locale: this.locale,
      baseFetch: this.baseFetch,
      fieldTypes: this.fieldTypes,
      backgroundColor: this.backgroundColor,
      withPhone: this.withPhone,
      withVerification: this.withVerification,
      withPayment: this.withPayment,
      isPaymentConnected: this.isPaymentConnected,
      withFormula: this.withFormula,
      withConditions: this.withConditions,
      isInlineSize: this.isInlineSize,
      defaultDrawFieldType: this.defaultDrawFieldType,
      selectedAreaRef: computed(() => this.selectedAreaRef),
      fieldsDragFieldRef: computed(() => this.fieldsDragFieldRef),
      selectedFieldUuids: computed(() => this.selectedFieldUuids),
      duplicatedFieldUuids: computed(() => this.duplicatedFieldUuids),
      alignmentFieldUuids: computed(() => this.alignmentFieldUuids),
      conditionPickModeRef: computed(() => ({ value: this.isConditionPickMode || this.isBulkConditionPickMode || this.isPageConditionPickMode })),
      pickConditionField: (field) => this.pickConditionField(field),
      submitterPeople: computed(() => this.submitterPeople)
    }
  },
  props: {
    template: {
      type: Object,
      required: true
    },
    i18n: {
      type: Object,
      required: false,
      default: () => ({})
    },
    withFieldPlaceholder: {
      type: Boolean,
      required: false,
      default: false
    },
    withSignatureId: {
      type: Boolean,
      required: false,
      default: null
    },
    withDownload: {
      type: Boolean,
      required: false,
      default: false
    },
    backgroundColor: {
      type: String,
      required: false,
      default: ''
    },
    locale: {
      type: String,
      required: false,
      default: ''
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    envelopeId: {
      type: String,
      required: false,
      default: () => ''
    },
    envelopeData: {
      type: String,
      required: false,
      default: () => null
    },
    withSendButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withSignYourselfButton: {
      type: Boolean,
      required: false,
      default: true
    },
    inputMode: {
      type: Boolean,
      required: false,
      default: false
    },
    withHelp: {
      type: Boolean,
      required: false,
      default: true
    },
    withFieldsDetection: {
      type: Boolean,
      required: false,
      default: false
    },
    withAddPageButton: {
      type: Boolean,
      required: false,
      default: false
    },
    autosave: {
      type: Boolean,
      required: false,
      default: true
    },
    defaultFields: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultRequiredFields: {
      type: Array,
      required: false,
      default: () => []
    },
    withSelectedFieldType: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultDrawFieldType: {
      type: String,
      required: false,
      default: 'text'
    },
    currencies: {
      type: Array,
      required: false,
      default: () => []
    },
    fieldTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    defineSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    acceptFileTypes: {
      type: String,
      required: false,
      default: 'image/*, application/pdf, application/zip'
    },
    baseUrl: {
      type: String,
      required: false,
      default: ''
    },
    withLogo: {
      type: Boolean,
      required: false,
      default: true
    },
    onUpload: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    onSave: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    onChange: {
      type: Function,
      required: false,
      default () {
        return () => {}
      }
    },
    withStickySubmitters: {
      type: Boolean,
      required: false,
      default: true
    },
    withUploadButton: {
      type: Boolean,
      required: false,
      default: true
    },
    withTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    withFieldsSearch: {
      type: Boolean,
      required: false,
      default: null
    },
    withFieldsList: {
      type: Boolean,
      required: false,
      default: true
    },
    authenticityToken: {
      type: String,
      required: false,
      default: ''
    },
    withDocumentsList: {
      type: Boolean,
      required: false,
      default: true
    },
    withReplaceAndCloneUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    withPhone: {
      type: Boolean,
      required: false,
      default: false
    },
    withVerification: {
      type: Boolean,
      required: false,
      default: null
    },
    withPayment: {
      type: Boolean,
      required: false,
      default: false
    },
    isPaymentConnected: {
      type: Boolean,
      required: false,
      default: false
    },
    withFormula: {
      type: Boolean,
      required: false,
      default: false
    },
    withConditions: {
      type: Boolean,
      required: false,
      default: false
    },
    withGoogleDrive: {
      type: Boolean,
      required: false,
      default: false
    },
    onlyDefinedFields: {
      type: Boolean,
      required: false,
      default: false
    },
    fetchOptions: {
      type: Object,
      required: false,
      default: () => ({ headers: {} })
    },
    showTourStartForm: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      // Anything in this template that reaches a child through $refs has to wait for mount:
      // $refs is empty during the first render, so a bare `$refs.fields && ...` paints one
      // frame of wrong state and never re-renders on its own. Flipped in mounted(), which
      // makes every expression keyed to it re-evaluate exactly once.
      mountedRefs: false,
      documentRefs: [],
      zoom: 1,
      isBreakpointLg: false,
      isDownloading: false,
      isLoadingBlankPage: false,
      isSaving: false,
      savePromise: null,
      pendingSaveCount: 0,
      saveError: null,
      selectedSubmitter: null,
      showDrawField: false,
      pendingFieldAttachmentUuids: [],
      drawField: null,
      copiedArea: null,
      drawFieldType: null,
      pendingSmartField: null,
      drawOption: null,
      dragField: null,
      isDragFile: false,
      isShortcutsModal: false,
      isAdvancedOptionsModal: false,
      isActionsDropdown: false,
      hoveredAction: '',
      envelopeParsed: null,
      isEditMessageModal: false,
      isEditRecipientsModal: false,
      // Saved email wording (MessageTemplatesController), fetched the first time Edit Message is
      // opened rather than on mount: most sessions in this builder never open that dialog.
      messageTemplates: [],
      messageTemplatesLoaded: false,
      messageTemplateName: '',
      messageTemplateSaving: false,
      messageTemplateError: '',
      // Ticked state of the Edit Recipients modal's "Set signing order" box. Seeded from the
      // fetched recipients when the modal opens, then owned by the modal until Save.
      envelopeEditSigningOrder: false,
      // Recipients as last written by this session, replacing the ones parsed out of
      // data-envelope-data. The dataset attribute is a snapshot taken when the page rendered,
      // so without this a routing number saved from the Signers panel would show the old value
      // until a reload.
      envelopeRecipientOverrides: null,
      // Guards the Signers panel's order controls while a PATCH is in flight: the envelope side
      // writes through the server, and a second change sent before the first lands would race.
      signingOrderSaving: false,
      envelopeEditMessage: { subject: '', body: '' },
      envelopeEditRecipients: [],
      advActiveSection: 'recipient-privileges',
      advSaving: false,
      advSections: [
        { id: 'recipient-privileges', label: 'Recipient Privileges' },
        { id: 'reminders', label: 'Reminders' },
        { id: 'expiration', label: 'Expiration' },
        { id: 'template-password', label: 'Template Password' },
        { id: 'template-modification', label: 'Template Modification' }
      ],
      advEdit: {
        can_sign_on_paper: false,
        can_delegate: true,
        reminder_enabled: false,
        reminder_days: 3,
        reminder_frequency: 3,
        expire_after_days: 120,
        expiration_alert_days: 0,
        password_enabled: false,
        password: '',
        allow_recipients_edit: true,
        allow_message_edit: true
      },
      selectedPanelField: null,
      isPreviewCollapsed: false,
      selectedFormulaField: null,
      selectedFontField: null,
      selectedDescriptionField: null,
      selectedConditionField: null,
      isConditionPickMode: false,
      conditionPickSourceField: null,
      conditionPickAction: '',
      conditionPickValue: '',
      isBulkConditionPickMode: false,
      bulkConditionPickSourceField: null,
      bulkConditionPickAction: '',
      bulkConditionPickValue: '',
      isPageConditionPickMode: false,
      pageConditionItem: null,
      pageConditionSourceField: null,
      pageConditionAction: '',
      pageConditionValue: ''

    }
  },
  computed: {
    fieldNames: FieldType.computed.fieldNames,
    // The recipient the whole multi-selection belongs to, or '' when it spans more than one.
    // Delegates to fields.vue rather than re-deriving it: that component owns the selection.
    // Safe as a computed even though it goes through $refs -- the method it calls reads
    // selectedFieldUuids.value and template.fields, both reactive, so the cache invalidates
    // whenever the selection or a field's owner changes.
    bulkSubmitterUuid () {
      if (!this.mountedRefs || !this.$refs.fields) {
        return ''
      }

      return this.$refs.fields.bulkSubmitterUuid()
    },
    // Label for the type awaiting placement, matching the sidebar button's own wording.
    placeFieldName () {
      const type = this.drawField?.type || this.drawFieldType

      return this.fieldNames[type] || 'field'
    },
    // Templates created from an existing one are stored as "<pdf name>_<uuid>" to keep names
    // unique. The uuid is noise in the header (it wrapped onto a second line), so hide it —
    // the full name stays in the database and is available via the title attribute.
    displayName () {
      return (this.template.name || '').replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '')
    },
    submitterDefaultNames: FieldSubmitter.computed.names,
    // submitter uuid -> the person filling that role, so the field dropdown can show both.
    // template.submitters holds only SIGNERS once an envelope has been through the role-picker
    // filter (see EnvelopesController#prepare) -- a cc/viewer recipient never gets a submitter
    // slot, so raw recipient index no longer lines up with template.submitters once the mix
    // includes one. An older envelope built before that filter existed still has exactly one
    // recipient per submitter, cc/viewer included, and there's no stored flag saying which shape
    // a given template used. Infer it from size the same way the server does
    // (EnvelopesController#get_recipients): a filtered array is strictly shorter than the
    // recipient list as soon as there's a non-signer; an unfiltered one is always equal.
    submitterPeople () {
      return (this.template.submitters || []).reduce((acc, submitter, index) => {
        const name = this.signingEnvelopeRecipients[index]?.name

        if (name && name !== submitter.name) acc[submitter.uuid] = name

        return acc
      }, {})
    },
    // Parsed once for every consumer below: submitterPeople (who fills each role), the action
    // type on each Signers row, and the Copied / Viewers list. Empty for a plain template --
    // ?envelope= is what puts recipients in data-envelope-data at all (templates/edit.html.erb).
    envelopeRecipients () {
      if (this.envelopeRecipientOverrides) return this.envelopeRecipientOverrides

      if (!this.envelopeData) return []

      try {
        return JSON.parse(this.envelopeData)?.envelope_recipients || []
      } catch {
        return []
      }
    },
    // Signing order is a routing NUMBER per signer -- DocuSign's model -- and never a position
    // in a list. Two signers sharing a number are invited together; a lower number goes first
    // (lib/submitters.rb groups submitters by it). Nothing in this feature reorders
    // template.submitters or envelope_recipients: both are paired by position
    // (Envelopes::SendSubmission, and the `-> { order(:id) }` scope on envelope_recipients), so
    // moving a row would hand one signer another's role.
    //
    // Which record holds the number depends on what is open:
    //   envelope       -> envelope_recipients.signing_order. Envelopes::SendSubmission reads it,
    //                     flips the submission to submitters_order: 'preserved', and stamps it
    //                     onto the matching template submitter at send time -- so a value
    //                     written to the submitter here would simply be overwritten.
    //   plain template -> template.submitters[].order, which Submissions.create_from_submitters
    //                     groups by. There are no envelope recipients to write to.
    isEnvelopeBuilder () {
      return !!this.envelopeId
    },
    signingOrderEnabled () {
      if (this.isEnvelopeBuilder) {
        return this.signingEnvelopeRecipients.some((r) => (r.signing_order || 0) > 0)
      }

      return (this.template.submitters || []).every((s) => s.order)
    },
    // One number per entry of template.submitters, in that array's own order. Falls back to
    // position + 1 so the inputs still read sensibly for a signer that has never been given a
    // number (an envelope built before this control existed).
    signerOrders () {
      return (this.template.submitters || []).map((submitter, index) => {
        if (this.isEnvelopeBuilder) {
          return this.signingEnvelopeRecipients[index]?.signing_order || (index + 1)
        }

        return submitter.order || (index + 1)
      })
    },
    // The signer recipients, positionally paired with template.submitters. Size inference per
    // the comment on submitterPeople above.
    signingEnvelopeRecipients () {
      const recipients = this.envelopeRecipients
      const submitters = this.template.submitters || []

      return submitters.length < recipients.length
        ? recipients.filter((r) => r.role === 'signer' || !r.role)
        : recipients
    },
    // submitter uuid -> action-type label. Deliberately empty for a plain template: submitters
    // are roles, and no action type exists on them until an envelope is created, so the row
    // says "set when sending" rather than asserting a "Needs to Sign" that nothing stores.
    submitterActionTypes () {
      if (!this.envelopeData) return {}

      return (this.template.submitters || []).reduce((acc, submitter, index) => {
        const role = this.signingEnvelopeRecipients[index]?.role

        acc[submitter.uuid] = RECIPIENT_ROLE_LABELS[role] || RECIPIENT_ROLE_LABELS.signer

        return acc
      }, {})
    },
    // cc/viewer recipients fill nothing, so they get no submitter slot and appear nowhere else
    // in the builder -- an envelope's CC was invisible here until this list.
    nonSigningRecipients () {
      return this.envelopeRecipients.filter((r) => r.role && r.role !== 'signer')
    },
    // Module constant reached through a computed because a template can't see one directly.
    recipientRoleLabels () {
      return RECIPIENT_ROLE_LABELS
    },
    // Only picks which modifier the hint names: both gestures already accept ctrlKey OR metaKey
    // whatever the platform (area.vue's onCtrlClick, page.vue's onStartDraw), so a wrong guess
    // mislabels the shortcut, it doesn't break it.
    isMac () {
      return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent)
    },
    selectedAreaRef: () => ref(),
    fieldsDragFieldRef: () => ref(),
    selectedFieldUuids: () => ref(new Set()),
    duplicatedFieldUuids: () => ref(new Set()),
    alignmentFieldUuids: () => ref(new Set()),
    language () {
      return this.locale.split('-')[0].toLowerCase()
    },
    withPrefillable () {
      if (this.template.fields) {
        return this.template.fields.some((f) => f.prefillable)
      } else {
        return false
      }
    },
    isInlineSize () {
      return CSS.supports('container-type: size')
    },
    isMobile () {
      const isMobileSafariIos = 'ontouchstart' in window && navigator.maxTouchPoints > 0 && /AppleWebKit/i.test(navigator.userAgent)

      return isMobileSafariIos || /android|iphone|ipad/i.test(navigator.userAgent)
    },
    defaultDateFormat () {
      const isUsBrowser = Intl.DateTimeFormat().resolvedOptions().locale.endsWith('-US')
      const isUsTimezone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).match(/\s(?:CST|CDT|PST|PDT|EST|EDT)$/)

      return this.localeDateFormats[this.locale] || ((isUsBrowser || isUsTimezone) ? 'MM/DD/YYYY' : 'DD/MM/YYYY')
    },
    localeDateFormats () {
      return {
        'de-DE': 'DD.MM.YYYY',
        'fr-FR': 'DD/MM/YYYY',
        'it-IT': 'DD/MM/YYYY',
        'en-GB': 'DD/MM/YYYY',
        'es-ES': 'DD/MM/YYYY'
      }
    },
    fieldAreasIndex () {
      const areas = {}

      this.template.fields.forEach((f) => {
        (f.areas || []).forEach((a) => {
          areas[a.attachment_uuid] ||= {}

          const acc = (areas[a.attachment_uuid][a.page] ||= [])
          acc.push({ area: a, field: f })
        })
      })

      return areas
    },
    isAllRequiredFieldsAdded () {
      return !this.defaultRequiredFields?.some((f) => {
        return !this.template.fields?.some((field) => field.name === f.name)
      })
    },
    selectedField () {
      return this.template.fields.find((f) => f.areas?.includes(this.selectedAreaRef.value))
    },
    fieldPanelTitle () {
      if (!this.selectedPanelField) return ''
      const names = { text: 'Text', signature: 'Signature', initials: 'Initials', date: 'Date', number: 'Number', image: 'Image', file: 'File', select: 'Select', checkbox: 'Checkbox', multiple: 'Multiple', radio: 'Radio', cells: 'Cells', stamp: 'Stamp', phone: 'Phone' }
      return names[this.selectedPanelField.type] || this.selectedPanelField.type
    },
    sortedDocuments () {
      return this.template.schema.map((item) => {
        return this.template.documents.find(doc => doc.uuid === item.attachment_uuid)
      })
    },
    // Same nine hues, in the same order, as the dots in field_submitter.vue and the field
    // borders/fills in area.vue, and as the two ERB colour arrays (submissions/show and
    // submission_events/index) -- a signer has one colour everywhere or the colour means
    // nothing. NOT a true invariant at indexes 2-3 today: #facc15 is yellow-400 where every
    // class-based palette says yellow-300, and #a855f7 is purple-500 where they say purple-600.
    // Deliberately left as-is -- recipients cap at two today, so those positions are
    // unreachable -- rather than asserted as matching when it doesn't hold.
    submitterColors () {
      return ['#10b981', '#3b82f6', '#facc15', '#a855f7', '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#6366f1']
    },
    // Types whose value is plain text, so the right panel can prefill them from an input.
    // Everything else (signature, initials, image, file, stamp, payment, and the option-based
    // types) has to be produced on the document itself.
    prefillableTypes () {
      return ['text', 'number', 'date', 'cells', 'phone']
    },
    // The required-field checklist, in document order so it reads down the page like the
    // fields list does. Placed fields only: one with no areas has nothing to jump to.
    requiredPanelFields () {
      return this.template.fields.filter((field) => field.required && field.areas?.length)
    }
  },
  created () {
    // Upstream localized an untouched default role here ("First Party" -> the locale's own
    // wording). The two roles are ApTask's own names now (SIGNER_ROLE_NAMES) and are not
    // translated, so a fresh template only needs the onboarding role's prefill filled in --
    // templates created before that prefill existed still carry the bare name.
    if (!this.template.fields?.length && this.template.submitters?.length === 1) {
      const first = this.template.submitters[0]

      if (first?.name && ROLE_DEFAULTS[first.name] && !first.email) {
        Object.assign(first, ROLE_DEFAULTS[first.name])
      }
    }

    const existingSubmittersUuids = this.defaultSubmitters.map((name) => {
      return this.template.submitters.find(e => e.name === name)?.uuid
    })

    this.defaultSubmitters.forEach((name, index) => {
      const submitter = (this.template.submitters[index] ||= {})

      submitter.name = name

      if (existingSubmittersUuids.filter(Boolean).length) {
        submitter.uuid = existingSubmittersUuids[index] || submitter.uuid || v4()
      } else {
        submitter.uuid ||= v4()
      }
    })

    const defineSubmittersUuids = this.defineSubmitters.map((name) => {
      return this.template.submitters.find(e => e.name === name)?.uuid
    })

    this.defineSubmitters.forEach((name, index) => {
      const submitter = (this.template.submitters[index] ||= {})

      submitter.name = name || this.submitterDefaultNames[index]

      if (defineSubmittersUuids.filter(Boolean).length || existingSubmittersUuids.filter(Boolean).length) {
        submitter.uuid = defineSubmittersUuids[index] || existingSubmittersUuids[index] || submitter.uuid || v4()
      } else {
        submitter.uuid ||= v4()
      }
    })

    this.selectedSubmitter = this.template.submitters[0]
  },
  mounted () {
    this.mountedRefs = true
    this.undoStack = [JSON.stringify(this.template)]
    this.redoStack = []

    window.addEventListener('beforeunload', this.onBeforeUnload)

    this.$watch(
      () => this.isAdvancedOptionsModal,
      (val) => {
        if (val) {
          const p = this.template.preferences || {}
          this.advEdit = {
            can_sign_on_paper: false,
            can_delegate: p.can_delegate !== false,
            reminder_enabled: p.reminder_enabled === true,
            reminder_days: p.reminder_days || 3,
            reminder_frequency: p.reminder_frequency || 3,
            expire_after_days: p.expire_after_days || 120,
            expiration_alert_days: p.expiration_alert_days || 0,
            password_enabled: p.password_enabled === true,
            password: p.password || '',
            allow_recipients_edit: p.allow_recipients_edit !== false,
            allow_message_edit: p.allow_message_edit !== false
          }
        }
      }
    )

    this.$watch(
      () => this.selectedFieldUuids.value.size,
      (size) => {
        if (size > 0) {
          this.selectedPanelField = null
          this.selectedAreaRef.value = null
        }
      }
    )
    this.$watch(
      () => this.selectedAreaRef.value,
      (area) => {
        if (area) {
          const field = this.template.fields.find((f) => f.areas?.includes(area))
          if (field) {
            if (!this.isConditionPickMode && !this.isBulkConditionPickMode) {
              this.selectedPanelField = field
            }
          }
        } else {
          if (!this.isConditionPickMode && !this.isBulkConditionPickMode) {
            this.selectedPanelField = null
          }
        }
      }
    )

    this.$nextTick(() => {
      this.onWindowResize()
    })

    document.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('keydown', this.onKeyDown)

    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('dragleave', this.onWindowDragLeave)

    this.$nextTick(() => {
      if (document.location.search?.includes('stripe_connect_success')) {
        document.querySelector('form[action="/auth/stripe_connect"]')?.closest('.dropdown')?.querySelector('label')?.focus()
      }
    })

    this.template.schema.forEach((item) => {
      if (item.pending_fields) {
        this.pendingFieldAttachmentUuids.push(item.attachment_uuid)
      }
    })
  },
  unmounted () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)

    document.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('keydown', this.onKeyDown)

    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('dragleave', this.onWindowDragLeave)
  },
  beforeUpdate () {
    this.documentRefs = []
  },
  methods: {
    toRaw,
    // Only fires when an edit has not reached the server or the last save failed — autosave
    // means a blanket "unsaved changes" prompt on every exit would be a lie.
    onBeforeUnload (event) {
      if (!this.hasUnsavedWork()) {
        return
      }

      event.preventDefault()

      return (event.returnValue = 'Your latest changes have not been saved yet.')
    },
    download () {
      this.isDownloading = true

      this.baseFetch(`/templates/${this.template.id}/documents`).then(async (response) => {
        if (response.ok) {
          const urls = await response.json()
          const isMobileSafariIos = 'ontouchstart' in window && navigator.maxTouchPoints > 0 && /AppleWebKit/i.test(navigator.userAgent)
          const isSafariIos = isMobileSafariIos || /iPhone|iPad|iPod/i.test(navigator.userAgent)

          if (isSafariIos && urls.length > 1) {
            this.downloadSafariIos(urls)
          } else {
            this.downloadUrls(urls)
          }
        } else {
          alert(this.t('failed_to_download_files'))
        }
      })
    },
    downloadUrls (urls) {
      const fileRequests = urls.map((url) => {
        return () => {
          return fetch(url).then(async (resp) => {
            const blobUrl = URL.createObjectURL(await resp.blob())
            const link = document.createElement('a')

            link.href = blobUrl
            link.setAttribute('download', decodeURI(url.split('/').pop()))

            link.click()

            URL.revokeObjectURL(blobUrl)
          })
        }
      })

      fileRequests.reduce(
        (prevPromise, request) => prevPromise.then(() => request()),
        Promise.resolve()
      ).finally(() => {
        this.isDownloading = false
      })
    },
    downloadSafariIos (urls) {
      const fileRequests = urls.map((url) => {
        return fetch(url).then(async (resp) => {
          const blob = await resp.blob()
          const blobUrl = URL.createObjectURL(blob.slice(0, blob.size, 'application/octet-stream'))
          const link = document.createElement('a')

          link.href = blobUrl
          link.setAttribute('download', decodeURI(url.split('/').pop()))

          return link
        })
      })

      Promise.all(fileRequests).then((links) => {
        links.forEach((link, index) => {
          setTimeout(() => {
            link.click()

            URL.revokeObjectURL(link.href)
          }, index * 50)
        })
      }).finally(() => {
        this.isDownloading = false
      })
    },
    onDragover (e) {
      if (this.$refs.dragPlaceholder?.dragPlaceholder) {
        this.$refs.dragPlaceholder.isMask = e.target.id === 'mask'

        const ref = this.$refs.dragPlaceholder.dragPlaceholder

        ref.x = e.clientX - ref.offsetX
        ref.y = e.clientY - ref.offsetY
      } else if (e.dataTransfer?.types?.includes('Files')) {
        this.isDragFile = true
      }
    },
    onWindowDragLeave (event) {
      if (event.clientX <= 0 || event.clientY <= 0 || event.clientX >= window.innerWidth || event.clientY >= window.innerHeight) {
        this.isDragFile = false
      }
    },
    reorderFields (item) {
      const itemFields = []
      const fields = []
      const fieldAreasIndex = {}

      const attachmentUuids = this.template.schema.map((e) => e.attachment_uuid)

      this.template.fields.forEach((f) => {
        if (f.areas?.length) {
          const firstArea = f.areas.reduce((min, a) => {
            return attachmentUuids.indexOf(a.attachment_uuid) < attachmentUuids.indexOf(min.attachment_uuid) ? a : min
          }, f.areas[0])

          if (firstArea.attachment_uuid === item.attachment_uuid) {
            itemFields.push(f)
          } else {
            fields.push(f)
          }
        } else {
          fields.push(f)
        }
      })

      const sortArea = (aArea, bArea) => {
        if (aArea.attachment_uuid === bArea.attachment_uuid) {
          if (aArea.page === bArea.page) {
            if (Math.abs(aArea.y - bArea.y) < 0.01) {
              if (aArea.x === bArea.x) {
                return 0
              } else {
                return aArea.x - bArea.x
              }
            } else {
              return aArea.y - bArea.y
            }
          } else {
            return aArea.page - bArea.page
          }
        } else {
          return attachmentUuids.indexOf(aArea.attachment_uuid) - attachmentUuids.indexOf(bArea.attachment_uuid)
        }
      }

      itemFields.sort((aField, bField) => {
        const aArea = (fieldAreasIndex[aField.uuid] ||= [...(aField.areas || [])].sort(sortArea)[0])
        const bArea = (fieldAreasIndex[bField.uuid] ||= [...(bField.areas || [])].sort(sortArea)[0])

        return sortArea(aArea, bArea)
      })

      const insertBeforeAttachmentUuids = attachmentUuids.slice(this.template.schema.indexOf(item) + 1)

      let sortedFields = []

      if (insertBeforeAttachmentUuids.length) {
        const insertAfterField = fields.find((f) => {
          if (f.areas?.length) {
            return f.areas.find((a) => insertBeforeAttachmentUuids.includes(a.attachment_uuid))
          } else {
            return false
          }
        })

        if (insertAfterField) {
          fields.splice(fields.indexOf(insertAfterField), 0, ...itemFields)

          sortedFields = fields
        } else {
          sortedFields = fields.concat(itemFields)
        }
      } else {
        if (fields.length && itemFields.length && this.template.fields.indexOf(fields[0]) > this.template.fields.indexOf(itemFields[0])) {
          sortedFields = itemFields.concat(fields)
        } else {
          sortedFields = fields.concat(itemFields)
        }
      }

      if (this.template.fields.length === sortedFields.length) {
        this.template.fields = sortedFields
        this.save()
      }
    },
    closeDropdown () {
      document.activeElement.blur()
    },
    t (key) {
      return this.i18n[key] || i18n[this.language]?.[key] || i18n.en[key] || key
    },
    removePendingFields () {
      this.template.fields = this.template.fields.filter((f) => {
        return this.template.schema.find((item) => item.attachment_uuid === f.attachment_uuid && item.pending_fields)
      })

      this.save()
    },
    addField (type, area = null) {
      const field = {
        name: '',
        uuid: v4(),
        required: type !== 'checkbox',
        areas: area ? [area] : [],
        submitter_uuid: this.selectedSubmitter.uuid,
        type
      }

      if (['select', 'multiple', 'radio'].includes(type)) {
        field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
      }

      if (type === 'stamp') {
        field.readonly = true
      }

      if (type === 'datenow') {
        field.type = 'date'
        field.readonly = true
        field.default_value = '{{date}}'
      }

      if (type === 'date') {
        field.preferences = {
          format: this.defaultDateFormat
        }
      }

      if (field.type === 'strikethrough') {
        field.readonly = true
        field.default_value = true
      }

      if (type === 'signature' && [true, false].includes(this.withSignatureId)) {
        field.preferences ||= {}
        field.preferences.with_signature_id = this.withSignatureId
      }
      if (this.pendingSmartField) {
        const sf = this.pendingSmartField
        field.name = sf.name || field.name
        // name is the snake_case data key ("ssn"); title carries the readable label ("SSN")
        // shown on the field box in the builder and above the field when signing.
        if (sf.label) field.title = sf.label
        if (sf.validation) field.validation = { ...sf.validation }
        this.pendingSmartField = null
      }

      // A field name is the data key, so two fields sharing one means one datum -- placing
      // "Full Name" a second time has to widen the existing field with another area, not mint
      // a second field. Two fields meant two steps and two confirmations while signing, and a
      // value typed into one left the other blank. Only named fields merge; the unnamed field
      // a bare type click produces has no datum to share yet.
      if (field.name && area) {
        const existing = this.template.fields.find((f) => {
          return f.submitter_uuid === field.submitter_uuid && f.type === field.type && f.name === field.name
        })

        if (existing) {
          this.addAreaToField(existing, area)

          this.save()

          return
        }
      }

      this.template.fields.push(field)

      this.save()
    },
    // Areas are read back in list order on the signing page, so a later page's area must not
    // land before an earlier one's.
    addAreaToField (field, area) {
      field.areas ||= []

      const insertBeforeIndex = field.areas.findIndex((a) => {
        return a.attachment_uuid === area.attachment_uuid && a.page > area.page
      })

      if (insertBeforeIndex === -1) {
        field.areas.push(area)
      } else {
        field.areas.splice(insertBeforeIndex, 0, area)
      }
    },
    startFieldDraw ({ name, type }) {
      const existingField = this.template.fields?.find((f) => f.submitter_uuid === this.selectedSubmitter.uuid && name && name === f.name)

      if (existingField) {
        this.drawField = existingField
      } else {
        const field = {
          name: name || '',
          uuid: v4(),
          required: type !== 'checkbox',
          areas: [],
          submitter_uuid: this.selectedSubmitter.uuid,
          type
        }

        if (['select', 'multiple', 'radio'].includes(type)) {
          field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
        }

        if (type === 'stamp') {
          field.readonly = true
        }

        if (type === 'date') {
          field.preferences = {
            format: this.defaultDateFormat
          }
        }

        this.drawField = field
      }

      this.drawOption = null
    },
    undo () {
      if (this.undoStack.length > 1) {
        this.undoStack.pop()
        const stringData = this.undoStack[this.undoStack.length - 1]
        const currentStringData = JSON.stringify(this.template)

        if (stringData && stringData !== currentStringData) {
          this.redoStack.push(currentStringData)

          Object.assign(this.template, JSON.parse(stringData))

          this.save()
        }
      }
    },
    redo () {
      const stringData = this.redoStack.pop()
      this.lastRedoData = stringData
      const currentStringData = JSON.stringify(this.template)

      if (stringData && stringData !== currentStringData) {
        if (this.undoStack[this.undoStack.length - 1] !== currentStringData) {
          this.undoStack.push(currentStringData)
        }

        Object.assign(this.template, JSON.parse(stringData))

        this.save()
      }
    },
    onWindowResize (e) {
      const breakpointLg = 1024

      this.isBreakpointLg = this.$el.getRootNode().querySelector('div[data-v-app]').offsetWidth < breakpointLg
    },
    setDocumentRefs (el) {
      if (el) {
        this.documentRefs.push(el)
      }
    },
    scrollIntoDocument (item) {
      const ref = this.documentRefs.find((e) => e.document.uuid === item.attachment_uuid)

      ref.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    clearDrawField () {
      this.drawField = null
      this.drawOption = null
      this.showDrawField = false

      if (!this.withSelectedFieldType) {
        this.drawFieldType = ''
      }
    },
    onKeyUp (e) {
      if (e.code === 'Escape') {
        this.clearDrawField()

        this.selectedAreaRef.value = null
        this.selectedPanelField = null

        if (this.selectedFieldUuids.value.size > 0) {
          this.selectedFieldUuids.value = new Set()
        }
      }

      if (this.editable && ['Backspace', 'Delete'].includes(e.key) && document.activeElement === document.body) {
        if (this.selectedFieldUuids.value.size > 0) {
          this.removeSelectedFields()
        } else if (this.selectedAreaRef.value) {
          this.removeArea(this.selectedAreaRef.value)

          this.selectedAreaRef.value = null
        }
      }
    },
    onKeyDown (event) {
      if ((event.metaKey && event.shiftKey && event.key === 'z') || (event.ctrlKey && event.key === 'Z')) {
        event.stopImmediatePropagation()
        event.preventDefault()

        this.redo()
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.stopImmediatePropagation()
        event.preventDefault()

        this.undo()
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'c' && document.activeElement === document.body) {
        event.preventDefault()

        this.copiedArea = this.selectedAreaRef?.value
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'v' && this.copiedArea && document.activeElement === document.body) {
        event.preventDefault()

        this.pasteField()

      } else if (this.selectedAreaRef.value && ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key) && document.activeElement === document.body) {
        event.preventDefault()

        this.handleAreaArrows(event)
      }
    },
    handleAreaArrows (event) {
      if (!this.editable) {
        return
      }

      const area = this.selectedAreaRef.value
      const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
      const page = documentRef.pageRefs[area.page].$refs.image
      const rect = page.getBoundingClientRect()
      const diff = (event.shiftKey ? 5.0 : 1.0)

      if (event.key === 'ArrowRight' && event.altKey) {
        area.w = Math.min(Math.max(area.w + diff / rect.width, 0), 1 - area.x)
      } else if (event.key === 'ArrowLeft' && event.altKey) {
        area.w = Math.min(Math.max(area.w - diff / rect.width, 0), 1 - area.x)
      } else if (event.key === 'ArrowUp' && event.altKey) {
        area.h = Math.min(Math.max(area.h - diff / rect.height, 0), 1 - area.y)
      } else if (event.key === 'ArrowDown' && event.altKey) {
        area.h = Math.min(Math.max(area.h + diff / rect.height, 0), 1 - area.y)
      } else if (event.key === 'ArrowRight') {
        area.x = Math.min(Math.max(area.x + diff / rect.width, 0), 1 - area.w)
      } else if (event.key === 'ArrowLeft') {
        area.x = Math.min(Math.max(area.x - diff / rect.width, 0), 1 - area.w)
      } else if (event.key === 'ArrowUp') {
        area.y = Math.min(Math.max(area.y - diff / rect.height, 0), 1 - area.h)
      } else if (event.key === 'ArrowDown') {
        area.y = Math.min(Math.max(area.y + diff / rect.height, 0), 1 - area.h)
      }

      this.debouncedSave()
    },
    zoomIn () {
      this.zoom = Math.min(3, Math.round((this.zoom + 0.25) * 100) / 100)
    },
    zoomOut () {
      this.zoom = Math.max(0.5, Math.round((this.zoom - 0.25) * 100) / 100)
    },
    resetZoom () {
      this.zoom = 1
    },
    debouncedSave () {
      clearTimeout(this._saveTimeout)

      this._saveTimeout = setTimeout(() => {
        this.save()
      }, 700)
    },
    startPageConditionPick (item) {
      this.isPageConditionPickMode = true
      this.pageConditionItem = item
      this.pageConditionSourceField = null
      this.pageConditionAction = ''
      this.pageConditionValue = ''
      this.selectedPanelField = null
      this.$nextTick(() => {
        this.selectedAreaRef.value = null
      })
    },
    cancelPageConditionPick () {
      this.isPageConditionPickMode = false
      this.pageConditionItem = null
      this.pageConditionSourceField = null
      this.pageConditionAction = ''
      this.pageConditionValue = ''
    },
    savePageConditionPick () {
      if (!this.pageConditionSourceField || !this.pageConditionItem) return
      const condition = {
        field_uuid: this.pageConditionSourceField.uuid,
        action: this.pageConditionAction
      }
      if (this.pageConditionValue) {
        condition.value = this.pageConditionValue
      }
      this.pageConditionItem.conditions = [condition]
      this.cancelPageConditionPick()
      this.save()
    },
    removePageCondition () {
      if (this.pageConditionItem) {
        delete this.pageConditionItem.conditions
        this.cancelPageConditionPick()
        this.save()
      }
    },
    startBulkConditionPick () {
      this.isBulkConditionPickMode = true
      this.bulkConditionPickSourceField = null
      this.bulkConditionPickAction = ''
      this.bulkConditionPickValue = ''
    },
    cancelBulkConditionPick () {
      this.isBulkConditionPickMode = false
      this.bulkConditionPickSourceField = null
      this.bulkConditionPickAction = ''
      this.bulkConditionPickValue = ''
    },
    saveBulkConditionPick () {
      if (!this.bulkConditionPickSourceField) return
      const condition = {
        field_uuid: this.bulkConditionPickSourceField.uuid,
        action: this.bulkConditionPickAction
      }
      if (this.bulkConditionPickValue) {
        condition.value = this.bulkConditionPickValue
      }
      const uuids = [...this.selectedFieldUuids.value]
      this.template.fields.forEach(f => {
        if (uuids.includes(f.uuid)) {
          f.conditions = [condition]
        }
      })
      this.cancelBulkConditionPick()
      this.save()
    },
    startConditionPick () {
      this.isConditionPickMode = true
      this.conditionPickSourceField = null
      this.conditionPickAction = ''
      this.conditionPickValue = ''
    },
    cancelConditionPick () {
      this.isConditionPickMode = false
      this.conditionPickSourceField = null
      this.conditionPickAction = ''
      this.conditionPickValue = ''
    },
    pickConditionField (field) {
      if (this.isPageConditionPickMode) {
        if (field !== this.pageConditionItem) {
          this.pageConditionSourceField = field
          this.isPageConditionPickMode = false
          const actions = this.getConditionActions(field)
          this.pageConditionAction = actions[0] || ''
          this.pageConditionValue = ''
        }
        this.$nextTick(() => {
          this.selectedAreaRef.value = null
        })
        return
      }
      if (this.isBulkConditionPickMode) {
        if (![...this.selectedFieldUuids.value].includes(field.uuid)) {
          this.bulkConditionPickSourceField = field
          this.isBulkConditionPickMode = false
          const actions = this.getConditionActions(field)
          this.bulkConditionPickAction = actions[0] || ''
          this.bulkConditionPickValue = ''
        }
        return
      }
      if (!this.isConditionPickMode) return
      if (field === this.selectedPanelField) return
      this.conditionPickSourceField = field
      this.isConditionPickMode = false
      const actions = this.getConditionActions(field)
      this.conditionPickAction = actions[0] || ''
      this.conditionPickValue = ''
    },
    getConditionActions (field) {
      if (!field) return []
      if (field.type === 'checkbox') return ['checked', 'unchecked']
      if (['radio', 'select'].includes(field.type)) return ['equal', 'not_equal']
      if (field.type === 'multiple') return ['contains', 'does_not_contain']
      return ['not_empty', 'empty']
    },
    saveConditionPick () {
      if (!this.conditionPickSourceField || !this.selectedPanelField) return
      const condition = {
        field_uuid: this.conditionPickSourceField.uuid,
        action: this.conditionPickAction
      }
      if (this.conditionPickValue) {
        condition.value = this.conditionPickValue
      }
      this.selectedPanelField.conditions = [condition]
      this.cancelConditionPick()
      this.save()
    },
    removeCondition () {
      if (this.selectedPanelField) {
        delete this.selectedPanelField.conditions
        this.cancelConditionPick()
        this.save()
      }
    },
    updateFieldName (value) {
      if (this.selectedPanelField && value.trim()) {
        this.selectedPanelField.name = value.trim()
        this.save()
      }
    },
    buildDefaultFieldName (field, fields) {
      const names = { text: 'Text', signature: 'Signature', initials: 'Initials', date: 'Date', number: 'Number', image: 'Image', file: 'File', select: 'Select', checkbox: 'Checkbox', multiple: 'Multiple', radio: 'Radio', cells: 'Cells', stamp: 'Stamp', phone: 'Phone' }
      const typeName = names[field.type] || field.type
      const typeIndex = (fields || this.template.fields).filter((f) => f.type === field.type).indexOf(field)
      return field.name || `${typeName} ${typeIndex + 1}`
    },
    removeSelectedFields () {
      const uuids = [...this.selectedFieldUuids.value]

      if (!uuids.length) return

      uuids.forEach((uuid) => {
        const field = this.template.fields.find((f) => f.uuid === uuid)

        if (field) {
          this.template.fields.splice(this.template.fields.indexOf(field), 1)
          this.removeFieldConditions(field)
        }
      })

      this.selectedFieldUuids.value = new Set()
      this.selectedAreaRef.value = null
      this.selectedPanelField = null

      this.save()
    },
    removeFieldByArea (area) {
      if (!area) return
      const field = this.template.fields.find((f) => f.areas?.includes(area))
      if (field) {
        this.template.fields.splice(this.template.fields.indexOf(field), 1)
        this.removeFieldConditions(field)
        this.selectedPanelField = null
        this.selectedAreaRef.value = null
        this.save()
      }
    },
    removeArea (area) {
      const field = this.template.fields.find((f) => f.areas?.includes(area))

      field.areas.splice(field.areas.indexOf(area), 1)

      if (!field.areas.length) {
        this.template.fields.splice(this.template.fields.indexOf(field), 1)

        this.removeFieldConditions(field)
      }

      this.save()
    },
    removeFieldConditions (field) {
      this.template.fields.forEach((f) => {
        if (f.conditions) {
          f.conditions.forEach((c) => {
            if (c.field_uuid === field.uuid) {
              f.conditions.splice(f.conditions.indexOf(c), 1)
            }
          })
        }
      })

      this.template.schema.forEach((item) => {
        if (item.conditions) {
          item.conditions.forEach((c) => {
            if (c.field_uuid === field.uuid) {
              item.conditions.splice(item.conditions.indexOf(c), 1)
            }
          })
        }
      })
    },

    onMoveDuplicatedFields ({ dx, dy, uuids }) {
      if (!uuids || !uuids.length) return
      this.template.fields.forEach((f) => {
        if (uuids.includes(f.uuid)) {
          if (f.areas) {
            f.areas.forEach((a) => {
              a.x = Math.min(Math.max(a.x + dx, 0), 1 - a.w)
              a.y = Math.min(Math.max(a.y + dy, 0), 1 - a.h)
            })
          }
        }
      })
    },
    onDuplicateFields (uuids, callback) {
      if (!uuids || !uuids.length) return
      const fieldsToClone = this.template.fields.filter((f) => uuids.includes(f.uuid))
      const newFields = []
      fieldsToClone.forEach((field) => {
        const newField = JSON.parse(JSON.stringify(field))
        newField.uuid = v4()
        newField.name = field.name
        if (newField.areas) {
          newField.areas = newField.areas.map((a) => ({
            ...a,
            x: Math.min(a.x + 0.1, 1 - a.w),
            y: a.y
          }))
        }
        this.template.fields.push(newField)
        newFields.push(newField)
      })
      const allAreas = newFields.flatMap((f) => f.areas || [])
      if (allAreas.length > 0) {
        const minX = Math.min(...allAreas.map((a) => a.x))
        const minY = Math.min(...allAreas.map((a) => a.y))
        const maxX = Math.max(...allAreas.map((a) => a.x + a.w))
        const maxY = Math.max(...allAreas.map((a) => a.y + a.h))
        const newUuids = newFields.map((f) => f.uuid)
        const newRect = { x: minX - 0.01, y: minY - 0.01, w: maxX - minX + 0.02, h: maxY - minY + 0.02 }
        if (callback) callback(newUuids, newRect)
        this.duplicatedFieldUuids.value = new Set(newUuids)
      }
      this.save()
    },

    pasteField () {
      const field = this.template.fields.find((f) => f.areas?.includes(this.copiedArea))
      const currentArea = this.selectedAreaRef?.value || this.copiedArea

      if (field && currentArea) {
        const area = {
          ...JSON.parse(JSON.stringify(this.copiedArea)),
          attachment_uuid: currentArea.attachment_uuid,
          page: currentArea.page,
          x: currentArea.x,
          y: currentArea.y + currentArea.h * 1.3
        }

        if (['radio', 'multiple'].includes(field.type)) {
          this.copiedArea.option_uuid ||= field.options[0].uuid
          area.option_uuid = v4()

          const lastOption = field.options[field.options.length - 1]

          if (!field.areas.find((a) => lastOption.uuid === a.option_uuid)) {
            area.option_uuid = lastOption.uuid
          } else {
            field.options.push({ uuid: area.option_uuid })
          }

          field.areas.push(area)
        } else {
          this.template.fields.push({
            ...JSON.parse(JSON.stringify(field)),
            uuid: v4(),
            areas: [area]
          })
        }

        this.selectedAreaRef.value = area

        this.save()
      }
    },
    pushUndo () {
      const stringData = JSON.stringify(this.template)

      if (this.undoStack[this.undoStack.length - 1] !== stringData) {
        this.undoStack.push(stringData)

        if (this.lastRedoData !== stringData) {
          this.redoStack = []
        }
      }
    },
    // Keyboard placement. Mouse placement asks the user for a spot; Enter/Space has no pointer
    // to ask with, so the field lands on the page filling most of the viewport -- the page the
    // user is looking at. Falls back to a field with no area (the "add without drawing" path)
    // when no page is rendered yet, rather than throwing on a missing mask.
    addFieldToPageInView ({ type }) {
      const target = this.pageInView()

      if (!target) {
        this.addField(type)

        return
      }

      const area = { page: target.page, attachment_uuid: target.attachmentUuid, x: 0, y: 0, w: 0, h: 0 }

      this.setDefaultAreaSize(area, type, target.dims)

      // Centred horizontally, a little below the top of the page, so a run of keyboard-added
      // fields does not stack on the exact same pixel.
      const existingOnPage = this.template.fields.reduce((acc, f) => {
        return acc + (f.areas || []).filter((a) => a.page === area.page && a.attachment_uuid === area.attachment_uuid).length
      }, 0)

      area.x = 0.5 - (area.w / 2)
      area.y = Math.min(0.05 + (existingOnPage * area.h * 1.5), 1 - area.h)

      // addField, not onDraw. onDraw's no-drawField branch re-derives the type from the box
      // width ('checkbox' under 35px, else 'text') and only trusts drawFieldType to override it
      // -- and drawFieldType is unset here because place-mode was never armed. A Signature added
      // from the keyboard would have come out a Text field. addField also carries the smart-field
      // payload and the merge-by-name that click placement uses.
      this.addField(type, area)

      this.selectedAreaRef.value = area

      // A smart field arrives via set-smart-field, which also stamps drawFieldType. Left set,
      // the next stray click on the page would silently place another field of that type --
      // place-mode was never armed, so nothing else clears it. Same guard clearDrawField uses.
      if (!this.withSelectedFieldType) {
        this.drawFieldType = ''
      }
    },
    // The page whose rendered box covers the middle of the viewport, falling back to the first
    // page on screen at all. Measured off the page container, not #mask -- the mask is hidden
    // (and therefore 0x0) whenever no pointer interaction is in flight.
    pageInView () {
      const middle = window.innerHeight / 2
      let fallback = null

      for (const documentRef of this.documentRefs) {
        const pageRefs = documentRef.pageRefs || []

        for (let page = 0; page < pageRefs.length; page += 1) {
          const el = pageRefs[page]?.$el

          if (!el) continue

          const rect = el.getBoundingClientRect()

          if (!rect.height) continue

          const hit = { page, attachmentUuid: documentRef.document.uuid, dims: { w: rect.width, h: rect.height } }

          if (rect.top <= middle && rect.bottom >= middle) return hit
          if (!fallback && rect.bottom > 0 && rect.top < window.innerHeight) fallback = hit
        }
      }

      return fallback
    },
    // dims: the page's own box, for callers with no pointer interaction in flight. #mask is
    // v-show'n on drag/resize/draw only, so outside those it is display:none and every
    // clientWidth below reads 0 -- which would size the area NaN. The mask fills the page
    // exactly when it is shown, so the two give the same numbers.
    setDefaultAreaSize (area, type, dims = null) {
      const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
      const pageMask = documentRef.pageRefs[area.page].$refs.mask
      const maskW = dims ? dims.w : pageMask.clientWidth
      const maskH = dims ? dims.h : pageMask.clientHeight

      if (type === 'checkbox') {
        area.w = maskW / 30 / maskW
        area.h = (maskW / 30 / maskW) * (maskW / maskH)
      } else if (type === 'image') {
        area.w = maskW / 5 / maskW
        area.h = (maskW / 5 / maskW) * (maskW / maskH)
      } else if (type === 'signature' || type === 'stamp' || type === 'verification') {
        area.w = maskW / 5 / maskW
        area.h = (maskW / 5 / maskW) * (maskW / maskH) / 2
      } else if (type === 'initials') {
        area.w = maskW / 10 / maskW
        area.h = (maskW / 35 / maskW)
      } else if (type === 'strikethrough') {
        area.w = maskW / 5 / maskW
        area.h = (maskW / 70 / maskW)
      } else {
        area.w = maskW / 5 / maskW
        area.h = (maskW / 35 / maskW)
      }
    },
    onDraw ({ area, isTooSmall }) {
      if (this.isBulkConditionPickMode || this.isPageConditionPickMode) return
      if (this.selectedFieldUuids.value.size > 0) {
        this.selectedFieldUuids.value.clear()
        this.selectedFieldUuids.value = new Set([...this.selectedFieldUuids.value])
      }
      if (this.alignmentFieldUuids.value.size > 0) {
        this.alignmentFieldUuids.value.clear()
        this.alignmentFieldUuids.value = new Set([...this.alignmentFieldUuids.value])
      }
      if (this.drawField) {
        if (this.drawOption) {
          const areaWithoutOption = this.drawField.areas?.find((a) => !a.option_uuid)

          if (areaWithoutOption && !this.drawField.areas.find((a) => a.option_uuid === this.drawField.options[0].uuid)) {
            areaWithoutOption.option_uuid = this.drawField.options[0].uuid
          }

          area.option_uuid = this.drawOption.uuid
        }

        if (area.w === 0 || area.h === 0) {
          const previousArea = this.drawField.areas?.[this.drawField.areas.length - 1]

          if (this.selectedField?.type === this.drawField.type) {
            area.w = this.selectedAreaRef.value.w
            area.h = this.selectedAreaRef.value.h
          } else if (previousArea) {
            area.w = previousArea.w
            area.h = previousArea.h
          } else {
            this.setDefaultAreaSize(area, this.drawOption ? 'checkbox' : this.drawField?.type)
          }

          area.x -= area.w / 2
          area.y -= area.h / 2
        }

        this.addAreaToField(this.drawField, area)

        if (this.template.fields.indexOf(this.drawField) === -1) {
          this.template.fields.push(this.drawField)
        }

        this.drawField = null
        this.drawOption = null

        this.selectedAreaRef.value = area

        this.save()
      } else {
        const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
        const pageMask = documentRef.pageRefs[area.page].$refs.mask

        let type = (pageMask.clientWidth * area.w) < 35 ? 'checkbox' : 'text'

        if (this.drawFieldType) {
          type = this.drawFieldType
        } else if (this.defaultDrawFieldType && this.defaultDrawFieldType !== 'text') {
          type = this.defaultDrawFieldType
        } else if (this.fieldTypes.length !== 0 && !this.fieldTypes.includes(type)) {
          type = this.fieldTypes[0]
        }

        if (type === 'checkbox' && !this.drawFieldType && (this.template.fields[this.template.fields.length - 1]?.type === 'checkbox' || area.w)) {
          const previousField = [...this.template.fields].reverse().find((f) => f.type === type)
          const previousArea = previousField?.areas?.[previousField.areas.length - 1]

          if (previousArea || area.w) {
            const areaW = previousArea?.w || (30 / pageMask.clientWidth)
            const areaH = previousArea?.h || (30 / pageMask.clientHeight)

            if ((pageMask.clientWidth * area.w) < 5) {
              area.x = area.x - (areaW / 2)
              area.y = area.y - (areaH / 2)
            }

            area.w = areaW
            area.h = areaH
          }
        }

        if (this.drawFieldType && (area.w === 0 || area.h === 0)) {
          if (this.selectedField?.type === this.drawFieldType) {
            area.w = this.selectedAreaRef.value.w
            area.h = this.selectedAreaRef.value.h
          } else {
            this.setDefaultAreaSize(area, this.drawFieldType)
          }

          area.x -= area.w / 2
          area.y -= area.h / 2
        }

        if (area.w && (type !== 'checkbox' || this.drawFieldType || !isTooSmall)) {
          this.addField(type, area)

          this.selectedAreaRef.value = area
        }
      }
    },
    onDropfield (area) {
      if (this.$refs.dragPlaceholder) {
        this.$refs.dragPlaceholder.dragPlaceholder = null
      }

      if (!this.editable) {
        return
      }

      const field = this.fieldsDragFieldRef.value || {
        name: '',
        uuid: v4(),
        submitter_uuid: this.selectedSubmitter.uuid,
        required: this.dragField.type !== 'checkbox',
        ...this.dragField
      }

      if (!field.type) {
        field.type = 'text'
      }

      if (!this.fieldsDragFieldRef.value) {
        if (['select', 'multiple', 'radio'].includes(field.type)) {
          if (this.dragField?.options?.length) {
            field.options = this.dragField.options.map(option => ({ value: option, uuid: v4() }))
          } else {
            field.options = [{ value: '', uuid: v4() }, { value: '', uuid: v4() }]
          }
        }

        if (field.type === 'datenow') {
          field.type = 'date'
          field.readonly = true
          field.default_value = '{{date}}'
        }

        if (['stamp', 'heading', 'strikethrough'].includes(field.type)) {
          field.readonly = true

          if (field.type === 'strikethrough') {
            field.default_value = true
          }
        }

        if (field.type === 'date') {
          field.preferences ||= {}
          field.preferences.format ||= this.defaultDateFormat
        }

        if (field.type === 'signature' && [true, false].includes(this.withSignatureId)) {
          field.preferences ||= {}
          field.preferences.with_signature_id = this.withSignatureId
        }
      }
      if (this.pendingSmartField && !this.fieldsDragFieldRef.value) {
        field.name = this.pendingSmartField.name || field.name
        if (this.pendingSmartField.label) field.title = this.pendingSmartField.label
        if (this.pendingSmartField.validation) field.validation = { ...this.pendingSmartField.validation }
        this.pendingSmartField = null
      }

      const fieldArea = {
        x: (area.x - 6) / area.maskW,
        y: area.y / area.maskH,
        page: area.page,
        attachment_uuid: area.attachment_uuid
      }

      this.assignDropAreaSize(fieldArea, field, area)

      if (field.width) {
        delete field.width
      }

      if (field.height) {
        delete field.height
      }

      // Same merge addField does: a named field dropped a second time widens the existing one
      // with another area instead of minting a duplicate. This path builds and pushes its own
      // field rather than calling addField, so without this drag-and-drop and click-to-place
      // disagreed -- dragging Full Name twice gave two fields, clicking it twice gave one.
      // fieldsDragFieldRef means an existing field is being dragged to gain another area, which
      // already has its target and must not be redirected.
      if (!this.fieldsDragFieldRef.value && field.name) {
        const existing = this.template.fields.find((f) => {
          return f !== field && f.submitter_uuid === field.submitter_uuid &&
            f.type === field.type && f.name === field.name
        })

        if (existing) {
          this.addAreaToField(existing, fieldArea)

          this.selectedAreaRef.value = fieldArea

          this.save()

          document.activeElement?.blur()

          return
        }
      }

      field.areas ||= []

      field.areas.push(fieldArea)

      this.selectedAreaRef.value = fieldArea

      if (this.template.fields.indexOf(field) === -1) {
        this.template.fields.push(field)
      }

      this.save()

      document.activeElement?.blur()

      if (field.type === 'heading') {
        this.$nextTick(() => {
          const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
          const areaRef = documentRef.pageRefs[area.page].areaRefs.find((ref) => ref.area === this.selectedAreaRef.value)

          areaRef.isHeadingSelected = true

          areaRef.focusValueInput()
        })
      }
    },
    assignDropAreaSize (fieldArea, field, area) {
      const fieldType = field.type || 'text'

      const previousField = [...this.template.fields].reverse().find((f) => f.type === fieldType)

      let baseArea

      if (this.selectedField?.type === fieldType) {
        baseArea = this.selectedAreaRef.value
      } else if (previousField?.areas?.length) {
        baseArea = previousField.areas[previousField.areas.length - 1]
      } else {
        if (['checkbox'].includes(fieldType)) {
          baseArea = {
            w: area.maskW / 30 / area.maskW,
            h: area.maskW / 30 / area.maskW * (area.maskW / area.maskH)
          }
        } else if (fieldType === 'image') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH)
          }
        } else if (fieldType === 'signature' || fieldType === 'stamp' || fieldType === 'verification') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH) / 2
          }
        } else if (fieldType === 'initials') {
          baseArea = {
            w: area.maskW / 10 / area.maskW,
            h: area.maskW / 35 / area.maskW
          }
        } else if (fieldType === 'strikethrough') {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: area.maskW / 70 / area.maskW
          }
        } else {
          baseArea = {
            w: area.maskW / 5 / area.maskW,
            h: area.maskW / 35 / area.maskW
          }
        }
      }

      fieldArea.w = baseArea.w
      fieldArea.h = baseArea.h

      if (fieldType === 'cells') {
        fieldArea.cell_w = baseArea.cell_w || (baseArea.w / 5)
      }

      if (field.areas?.length) {
        const lastArea = field.areas[field.areas.length - 1]

        if (lastArea) {
          fieldArea.w = lastArea.w
          fieldArea.h = lastArea.h
        }
      }

      if (field.width) {
        fieldArea.w = field.width / area.maskW
      }

      if (field.height) {
        fieldArea.h = field.height / area.maskH
      }

      fieldArea.y = fieldArea.y - fieldArea.h / 2
    },
    addBlankPage () {
      this.isLoadingBlankPage = true

      const canvas = document.createElement('canvas')

      canvas.width = 816
      canvas.height = 1056

      const ctx = canvas.getContext('2d')

      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      canvas.toBlob((blob) => {
        const file = new File([blob], `Page ${this.template.schema.length + 1}.png`, { type: blob.type, lastModified: Date.now() })

        const formData = new FormData()
        formData.append('files[]', file)

        this.baseFetch(`/templates/${this.template.id}/documents`, {
          method: 'POST',
          body: formData
        }).then(async (resp) => {
          this.updateFromUpload(await resp.json())
        }).finally(() => {
          this.isLoadingBlankPage = false
        })
      }, 'image/png')
    },
    onUploadFailed (error) {
      if (error) alert(error)
    },
    updateFromUpload (data) {
      this.template.schema.push(...data.schema)
      this.template.documents.push(...data.documents)

      if (data.fields) {
        this.template.fields = data.fields
      }

      if (data.submitters) {
        this.template.submitters = data.submitters

        if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
          this.selectedSubmitter = this.template.submitters[0]
        }
      }

      this.$nextTick(() => {
        if (this.$refs.previews) {
          this.$refs.previews.scrollTop = this.$refs.previews.scrollHeight
        }

        this.scrollIntoDocument(data.schema[0])
      })

      if (this.template.name === 'New Document') {
        this.template.name = this.template.schema[0].name
      }

      if (this.onUpload) {
        this.onUpload(this.template)
      }

      this.save()

      data.documents.forEach((attachment) => {
        if (attachment.metadata?.pdf?.fields?.length) {
          this.pendingFieldAttachmentUuids.push(attachment.uuid)

          attachment.metadata.pdf.fields.forEach((field) => {
            field.submitter_uuid = this.selectedSubmitter.uuid

            this.template.fields.push(field)
          })
        }
      })
    },
    updateName (value) {
      this.template.name = value

      this.save()
    },
    async onDocumentRemove (item) {
      if (await showConfirm(this.t('are_you_sure_'))) {
        this.template.schema.splice(this.template.schema.indexOf(item), 1)

        const removedFieldUuids = []

        this.template.fields.forEach((field) => {
          [...(field.areas || [])].forEach((area) => {
            if (area.attachment_uuid === item.attachment_uuid) {
              field.areas.splice(field.areas.indexOf(area), 1)

              removedFieldUuids.push(field.uuid)
            }
          })
        })

        this.template.fields = this.template.fields.reduce((acc, f) => {
          if (removedFieldUuids.includes(f.uuid) && !f.areas?.length) {
            this.removeFieldConditions(f)
          } else {
            acc.push(f)
          }

          return acc
        }, [])

        this.save()
      }
    },
    onDocumentReplace (data) {
      const { replaceSchemaItem, schema, documents } = data
      const { google_drive_file_id, ...cleanedReplaceSchemaItem } = replaceSchemaItem

      this.template.schema.splice(this.template.schema.indexOf(replaceSchemaItem), 1, { ...cleanedReplaceSchemaItem, ...schema[0] })
      this.template.documents.push(...documents)

      if (data.fields) {
        this.template.fields = data.fields

        const removedFieldUuids = []

        this.template.fields.forEach((field) => {
          [...(field.areas || [])].forEach((area) => {
            if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
              field.areas.splice(field.areas.indexOf(area), 1)

              removedFieldUuids.push(field.uuid)
            }
          })
        })

        this.template.fields =
          this.template.fields.filter((f) => !removedFieldUuids.includes(f.uuid) || f.areas?.length)
      }

      if (data.submitters) {
        this.template.submitters = data.submitters

        if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
          this.selectedSubmitter = this.template.submitters[0]
        }
      }

      this.template.fields.forEach((field) => {
        (field.areas || []).forEach((area) => {
          if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
            area.attachment_uuid = schema[0].attachment_uuid
          }
        })
      })

      if (this.onUpload) {
        this.onUpload(this.template)
      }

      this.save()
    },
    onDocumentsReplace (data) {
      data.schema.forEach((schemaItem, index) => {
        const existingSchemaItem = this.template.schema[index]

        if (this.template.schema[index]) {
          this.onDocumentReplace({
            replaceSchemaItem: existingSchemaItem,
            schema: [schemaItem],
            documents: [data.documents.find((doc) => doc.uuid === schemaItem.attachment_uuid)]
          })
        } else {
          this.updateFromUpload({
            schema: [schemaItem],
            documents: [data.documents.find((doc) => doc.uuid === schemaItem.attachment_uuid)],
            fields: data.fields,
            submitters: data.submitters
          })
        }
      })
    },
    onDocumentsReplaceAndTemplateClone (template) {
      window.Turbo.visit(`/templates/${template.id}/edit`)
    },
    moveDocument (item, direction) {
      const currentIndex = this.template.schema.indexOf(item)

      this.template.schema.splice(currentIndex, 1)

      if (currentIndex + direction > this.template.schema.length) {
        this.template.schema.unshift(item)
      } else if (currentIndex + direction < 0) {
        this.template.schema.push(item)
      } else {
        this.template.schema.splice(currentIndex + direction, 0, item)
      }

      this.save()
    },
    advExpiryDate () {
      const d = new Date()
      d.setDate(d.getDate() + (this.advEdit.expire_after_days || 120))
      return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
    },
    async openEditMessage () {
      const env = this.envelopeData ? JSON.parse(this.envelopeData) : null
      this.envelopeEditMessage = {
        subject: env?.email_subject || '',
        body: env?.email_body || ''
      }
      this.messageTemplateName = ''
      this.messageTemplateError = ''
      this.isEditMessageModal = true

      // Opened before the fetch resolves on purpose: the dialog's own two inputs are what the
      // user came for, and the saved-message picker filling in a moment later costs nothing.
      if (!this.messageTemplatesLoaded) {
        this.messageTemplatesLoaded = true
        this.messageTemplates = await loadMessageTemplates()
      }
    },
    // Copies a saved subject/body into the dialog's inputs, then clears the select: nothing
    // records which saved message an envelope came from, so a stuck selection would assert a
    // link that does not exist -- and re-picking the same entry after editing the text would
    // fire no change event at all.
    applyMessageTemplate (event) {
      const picked = this.messageTemplates.find((t) => String(t.id) === event.target.value)

      if (picked) {
        this.envelopeEditMessage.subject = picked.subject || ''
        this.envelopeEditMessage.body = picked.body || ''
      }

      event.target.value = ''
    },
    // Files the wording currently typed in the dialog for reuse. Deliberately separate from
    // Save, which writes that wording onto this envelope -- neither has ever implied the other.
    async saveMessageTemplateFromModal () {
      this.messageTemplateSaving = true
      this.messageTemplateError = ''

      try {
        const saved = await saveMessageTemplate({
          name: this.messageTemplateName.trim(),
          subject: this.envelopeEditMessage.subject || '',
          body: this.envelopeEditMessage.body || ''
        })

        this.messageTemplates = [...this.messageTemplates, saved]
          .sort((a, b) => a.name.localeCompare(b.name))
        this.messageTemplateName = ''
      } catch (e) {
        this.messageTemplateError = e.message
      } finally {
        this.messageTemplateSaving = false
      }
    },
    async saveEditMessage () {
      const resp = await fetch(`/envelopes/${this.envelopeId}/edit_message`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content },
        body: JSON.stringify({ email_subject: this.envelopeEditMessage.subject, email_body: this.envelopeEditMessage.body })
      })
      if (resp.ok) {
        this.isEditMessageModal = false
        alert('Message updated successfully!')
      } else {
        alert('Failed to save. Please try again.')
      }
    },
    async openEditRecipients () {
      try {
        const resp = await fetch(`/envelopes/${this.envelopeId}/get_recipients`, {
          headers: { 'Accept': 'application/json' }
        })
        if (resp.ok) {
          const data = await resp.json()
          this.envelopeEditRecipients = (data.envelope_recipients || []).map(r => ({ ...r }))
        } else {
          throw new Error('Failed to fetch')
        }
      } catch (e) {
        const env = this.envelopeData ? JSON.parse(this.envelopeData) : null
        this.envelopeEditRecipients = (env?.envelope_recipients || []).map(r => ({ ...r }))
      }

      // Seed the checkbox from what is stored, and give every row a number to edit even when
      // nothing is stored yet -- an empty ORDER input has nothing for the user to adjust.
      this.envelopeEditSigningOrder = this.envelopeEditRecipients.some((r) => (r.signing_order || 0) > 0)
      this.envelopeEditRecipients.forEach((r, i) => {
        if (!r.signing_order) r.signing_order = i + 1
      })

      this.isEditRecipientsModal = true
    },
    // Unticking has to clear the stored numbers, not just hide the inputs: "unordered" is
    // expressed to the server as every signing_order being 0.
    toggleEditRecipientsOrder (enabled) {
      this.envelopeEditSigningOrder = enabled

      this.envelopeEditRecipients.forEach((r, i) => {
        r.signing_order = enabled ? (r.signing_order || i + 1) : 0
      })
    },
    // Signers panel: turn the whole feature on or off in one go. See the comment on
    // isEnvelopeBuilder for why the envelope and plain-template cases write to different records.
    async toggleSigningOrder (enabled) {
      if (this.isEnvelopeBuilder) {
        const recipients = this.envelopeRecipients.map((r) => ({ ...r }))
        let signerPosition = 0

        recipients.forEach((r) => {
          const isSigner = !r.role || r.role === 'signer'

          if (!isSigner) {
            r.signing_order = 0

            return
          }

          signerPosition += 1
          r.signing_order = enabled ? (r.signing_order || signerPosition) : 0
        })

        await this.saveRecipientOrders(recipients)

        return
      }

      (this.template.submitters || []).forEach((submitter, index) => {
        if (enabled) {
          submitter.order = submitter.order || (index + 1)
        } else {
          delete submitter.order
        }
      })

      this.save()
    },
    // One signer's routing number. Clamped to 1..signers so a typo cannot produce an order the
    // send-time grouping reads as "before everyone", and duplicates are allowed on purpose --
    // that is how DocuSign expresses "these two sign at the same time".
    async setSignerOrder (index, value) {
      const submitters = this.template.submitters || []
      const parsed = parseInt(value, 10)
      const order = Math.min(Math.max(Number.isNaN(parsed) ? index + 1 : parsed, 1), submitters.length)

      if (this.isEnvelopeBuilder) {
        const recipients = this.envelopeRecipients.map((r) => ({ ...r }))
        // signerOrders is indexed against template.submitters, which holds SIGNERS only once an
        // envelope has been through the role-picker filter -- so walk to the index-th signer
        // rather than indexing the raw recipient list, which may carry cc/viewer rows.
        const target = recipients.filter((r) => !r.role || r.role === 'signer')[index]

        if (!target) return

        target.signing_order = order

        await this.saveRecipientOrders(recipients)

        return
      }

      // Every submitter needs a number once any of them has one: lib/submitters.rb falls back to
      // the array index for a submitter with no `order`, mixing a 0-based position in with these
      // 1-based numbers and quietly putting an unnumbered signer first.
      submitters.forEach((submitter, i) => {
        submitter.order = i === index ? order : (submitter.order || i + 1)
      })

      this.save()
    },
    // Writes routing numbers through the same endpoint the Edit Recipients modal uses, so there
    // is one server path for recipient edits. Sends the whole list because that endpoint pairs
    // payload entries to stored rows by id (falling back to position), and a partial list would
    // shift that pairing.
    async saveRecipientOrders (recipients) {
      this.signingOrderSaving = true

      try {
        const resp = await fetch(`/envelopes/${this.envelopeId}/edit_recipients`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content
          },
          body: JSON.stringify({ recipients })
        })

        if (!resp.ok) throw new Error('Failed to save signing order')

        this.envelopeRecipientOverrides = recipients
      } catch (e) {
        alert('Could not save the signing order. Please try again.')
      } finally {
        this.signingOrderSaving = false
      }
    },
    async saveEditRecipients () {
      const resp = await fetch(`/envelopes/${this.envelopeId}/edit_recipients`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content },
        body: JSON.stringify({ recipients: this.envelopeEditRecipients })
      })
      if (resp.ok) {
        this.isEditRecipientsModal = false
        window.location.reload()
      } else {
        alert('Failed to save. Please try again.')
      }
    },
    editDocuments () {
      // Close actions dropdown and deselect field panel first
      this.isActionsDropdownOpen = false
      this.selectedPanelField = null

      this.$nextTick(() => {
        // Try ref first
        const uploadRef = this.$refs.upload
        if (uploadRef) {
          const fileInput = uploadRef.$el?.querySelector('input[type="file"]')
          if (fileInput) {
            fileInput.click()
            return
          }
        }
        // Fallback: find any file input in the document
        const fileInput = document.querySelector('#add_document_button input[type="file"]') ||
                          document.querySelector('input[type="file"][name="files[]"]') ||
                          document.querySelector('.sticky input[type="file"]') ||
                          document.querySelector('input[type="file"]')
        if (fileInput) fileInput.click()
      })
    },
    // Renamed from discardChanges: with autosave on it never discarded anything, it just left
    // the builder. Now it also waits for any pending save first.
    async closeBuilder () {
      await this.flushSave()

      if (this.saveError && !await showConfirm(`${this.saveError}. Your latest changes are not saved — leave anyway?`, { danger: true, confirmText: 'Leave anyway' })) {
        return
      }

      window.location.href = '/envelopes'
    },
    async deleteEnvelope () {
      if (await showConfirm('Are you sure you want to delete this envelope? This cannot be undone.')) {
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = `/envelopes/${this.envelopeId}`
        const method = document.createElement('input')
        method.type = 'hidden'
        method.name = '_method'
        method.value = 'DELETE'
        form.appendChild(method)
        const csrf = document.createElement('input')
        csrf.type = 'hidden'
        csrf.name = 'authenticity_token'
        csrf.value = document.querySelector('meta[name=csrf-token]')?.content || ''
        form.appendChild(csrf)
        document.body.appendChild(form)
        form.submit()
      }
    },
    async saveAdvancedOptions () {
      this.advSaving = true
      try {
        const prefs = {
          can_sign_on_paper: this.advEdit.can_sign_on_paper,
          can_delegate: this.advEdit.can_delegate,
          reminder_enabled: this.advEdit.reminder_enabled,
          reminder_days: this.advEdit.reminder_days,
          reminder_frequency: this.advEdit.reminder_frequency,
          expire_after_days: this.advEdit.expire_after_days,
          default_expire_at_duration: 'custom_days',
          default_expire_at_days: this.advEdit.expire_after_days,
          expiration_alert_days: this.advEdit.expiration_alert_days,
          password_enabled: this.advEdit.password_enabled,
          allow_recipients_edit: this.advEdit.allow_recipients_edit,
          allow_message_edit: this.advEdit.allow_message_edit
        }
        if (this.advEdit.password_enabled && this.advEdit.password) {
          prefs.password = this.advEdit.password
        }
        const response = await fetch(`/templates/${this.template.id}/preferences`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content },
          body: JSON.stringify({ template: { preferences: prefs } })
        })
        if (response.ok) {
          Object.assign(this.template.preferences || (this.template.preferences = {}), prefs)
          this.isAdvancedOptionsModal = false
        } else {
          alert('Failed to save. Please try again.')
        }
      } finally {
        this.advSaving = false
      }
    },
    maybeShowErrorTemplateAlert (e) {
      if (!this.isAllRequiredFieldsAdded) {
        e.preventDefault()

        const fields = this.defaultRequiredFields?.filter((f) => {
          return !this.template.fields?.some((field) => field.name === f.name)
        })

        if (fields?.length) {
          return alert(this.t('add_all_required_fields_to_continue') + ': ' + fields.map((f) => f.name).join(', '))
        }
      }

      if (!this.template.fields.length) {
        e.preventDefault()

        alert(this.t('please_draw_fields_to_prepare_the_document'))
      }
    },
    onSaveClick () {
      if (!this.isAllRequiredFieldsAdded) {
        const fields = this.defaultRequiredFields?.filter((f) => {
          return !this.template.fields?.some((field) => field.name === f.name)
        })

        if (fields?.length) {
          return alert(this.t('add_all_required_fields_to_continue') + ': ' + fields.map((f) => f.name).join(', '))
        }
      }

      if (!this.template.fields.length) {
        alert(this.t('please_draw_fields_to_prepare_the_document'))
      } else {
        this.isSaving = true
        this.save().then(() => {
          // save() resolves even on failure (it reports through saveError), so check before
          // navigating away — otherwise a failed save would silently look like a success.
          if (this.saveError) {
            return alert(`${this.saveError}. Please try again.`)
          }

          window.Turbo.visit(`/templates/${this.template.id}`)
        }).finally(() => {
          this.isSaving = false
        })
      }
    },
    scrollToArea (area) {
      const documentRef = this.documentRefs.find((a) => a.document.uuid === area.attachment_uuid)

      documentRef.scrollToArea(area)

      this.selectedAreaRef.value = area
    },
    // Deleting a signer from the right panel goes through the fields list's own removal, which
    // also drops that signer's fields and reassigns the selection -- doing it here by splicing
    // template.submitters would leave orphaned fields pointing at a uuid nobody owns.
    async removeSubmitter (submitter) {
      // The fields list only mounts with withFieldsList on desktop; without it there is no
      // owner for the cascade above, so the panel simply cannot delete a signer.
      if (!this.$refs.fields) return
      if (!(await showConfirm(this.t('are_you_sure_')))) return

      this.$refs.fields.removeSubmitter(submitter)
    },
    scrollToRequiredField (field) {
      const area = field.areas?.[0]

      if (area) {
        this.scrollToArea(area)
      }
    },
    // Prefilling a required field from the panel is the same edit as typing into the box on the
    // page: it sets the field's default value. Blank clears it back to unfilled.
    setRequiredFieldValue (field, value) {
      const trimmed = (value || '').trim()

      if ((field.default_value || '') === trimmed) return

      field.default_value = trimmed || undefined

      this.save()
    },
    baseFetch (path, options = {}) {
      return fetch(this.baseUrl + path, {
        ...options,
        headers: {
          'X-CSRF-Token': this.authenticityToken,
          ...this.fetchOptions.headers,
          ...options.headers
        }
      })
    },
    save ({ force } = { force: false }) {
      // Tripwire, not a validator: a required field with no placed area is the phantom-field
      // regression a browser-stolen click could write (see field_type_button.vue's onDragend
      // and fields.vue's onFieldTypeButtonClick). This only warns -- it must never block or
      // alter the save, so a save that would have succeeded still does. Dev-only: process.env.NODE_ENV is replaced by webpack based on shakapacker's
      // build mode, so this whole block (and the .every scan) is dropped from the production
      // bundle rather than merely skipped at runtime.
      if (process.env.NODE_ENV !== 'production') {
        const phantomField = this.template.fields.find((f) => !(f.areas && f.areas.length > 0) && f.required)

        if (phantomField) {
          console.warn('Required field with no placed area (phantom field):', phantomField.uuid, phantomField.type, phantomField.name)
        }
      }

      this.pendingFieldAttachmentUuids = []

      if (this.onChange) {
        this.onChange(this.template)
      }

      if (!this.autosave && !force) {
        return Promise.resolve({})
      }

      this.$nextTick(() => {
        if (this.$el.closest('template-builder')) {
          this.$el.closest('template-builder').dataset.template = JSON.stringify(this.template)
        }
      })

      this.pushUndo()

      // Tracked so navigating away can wait for an in-flight autosave (see flushSave) and so
      // the beforeunload guard only warns when work is genuinely at risk. Saves can overlap
      // (drags call save directly), hence a counter rather than a single boolean.
      this.pendingSaveCount += 1

      this.savePromise = this.baseFetch(`/templates/${this.template.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          template: {
            name: this.template.name,
            schema: this.template.schema,
            submitters: this.template.submitters,
            fields: this.template.fields
          }
        }),
        headers: { 'Content-Type': 'application/json' }
      }).then((response) => {
        // A failed autosave used to be swallowed: no ok check, no catch. Changes appeared to
        // save, then vanished on reload.
        if (!response.ok) {
          throw new Error(`Save failed with status ${response.status}`)
        }

        this.saveError = null

        if (this.onSave) {
          this.onSave(this.template)
        }
      }).catch((error) => {
        this.saveError = error.message || 'Save failed'

        console.error(error)
      }).finally(() => {
        this.pendingSaveCount -= 1
      })

      return this.savePromise
    },
    // Resolve once nothing is left to persist: run any debounced save immediately, then wait
    // for the request itself. Called before leaving the builder.
    flushSave () {
      if (this._saveTimeout) {
        clearTimeout(this._saveTimeout)
        this._saveTimeout = null

        this.save()
      }

      return this.savePromise || Promise.resolve()
    },
    // True while an edit has not reached the server yet, or the last attempt failed.
    hasUnsavedWork () {
      return !!(this._saveTimeout || this.pendingSaveCount > 0 || this.saveError)
    },
    async leaveBuilder (event) {
      event.preventDefault()

      // Read the href before awaiting: event.currentTarget is nulled once the handler yields.
      const href = event.currentTarget.getAttribute('href')

      await this.flushSave()

      if (this.saveError && !await showConfirm(`${this.saveError}. Your latest changes are not saved — leave anyway?`, { danger: true, confirmText: 'Leave anyway' })) {
        return
      }

      window.location.href = href
    }
  }
}
</script>

<style>
/* Zoom > 1 widens the #documents wrapper; force page previews to fill it so the
   raster image scales up with the field overlay instead of capping at natural width. */
#pages_container.pages-zoomed img {
  width: 100%;
  height: auto;
}

</style>
